import type { SupabaseClient } from '@supabase/supabase-js';
import { Database } from '@/types/supabase';
import { getSupabaseAdminClient } from '@/lib/supabase/admin';
import { createClient } from '@/lib/supabase/client';
import { eventModelV2 } from './eventModelV2';
import { isEventAliasAllowed } from './eventModel';
import {
  poolCorrectionSnapshot,
  planPoolAdvancementUndo,
} from '@/app/[locale]/event/[eventId]/libs/poolAdvancementCorrection';
import { courtLabel } from '@/app/[locale]/event/[eventId]/libs/venues';
import { loadVenueContextForEvent } from '@/app/[locale]/event/[eventId]/libs/venuesLoad';
import { gateRowAliases } from '@/app/[locale]/event/[eventId]/libs/aliasGate';
import type { DrawPlan } from '@/app/[locale]/event/[eventId]/libs/drawListPresentation';
import {
  calculateStandingsForDraw,
  calculateRoundRobinStandings,
} from '@/draw/core/standingsCalculator';
import type {
  StageStandings,
  AdvancementPlacement,
  PlayerStanding,
  LadderTemplate,
  DrawOptions,
} from '@/draw/types';
import { getDrawPositions } from '@/draw/core/constants';
import { getSeedPosition } from '@/draw/utils/seeding';
import {
  calculateQualifyingPoolAdvancements,
  applySeparationLogic,
} from '@/draw/utils/qualifyingAdvancement';
import { resolveAdvancementTiers, tierDrawSize } from '@/draw/utils/advancementTiers';
import { resolveRoundRobinSettings } from '@/draw/utils/roundRobinSettings';
import {
  buildSeatPlan,
  unplannedSeatPositions,
  type SeatPlanEntry,
  type TierStageSettings,
  WITHDRAWN_OCCUPANT_REASON,
  LUCKY_LOSER_OCCUPANT_REASON,
} from '@/draw/utils/seatPlan';
import { getTemplate } from '@/draw/templates/ladderTemplates';
import {
  mapStandingsToLadderSlots,
  mapPoolStandingsToLadderSlots,
} from '@/draw/core/templateAdvancement';
import { POOL_PLAYOFFS_TEMPLATE_ID } from '@/draw/templates/placementLadder';
import type { MatchOutcome } from '@/draw/core/matchOutcome';

type Draw = Database['public']['Tables']['draw']['Row'];
type DrawInsert = Database['public']['Tables']['draw']['Insert'];
type DrawUpdate = Database['public']['Tables']['draw']['Update'];
type EventGroup = Database['public']['Tables']['event_group']['Row'];
type EventGroupRegistration = Database['public']['Tables']['event_group_registration']['Row'];
type DrawStage = Database['public']['Tables']['draw_stage']['Row'];
type DrawRound = Database['public']['Tables']['draw_round']['Row'];
type DrawMatch = Database['public']['Tables']['draw_match']['Row'];
type DrawSeeding = Database['public']['Tables']['draw_seeding']['Row'];
type Player = Database['public']['Tables']['player']['Row'];

type DrawWithGroup = Draw & {
  event_group: Pick<
    EventGroup,
    'id' | 'name' | 'group_type' | 'max_participants' | 'sort_order' | 'is_team'
  >;
};

type DrawListItem = DrawWithGroup & Required<DrawPlan>;

type GroupRegistrant = EventGroupRegistration & {
  player: Pick<
    Player,
    'id' | 'first_name' | 'last_name' | 'username' | 'alias' | 'current_rating' | 'profile_photo'
  >;
};

type EventMatchSchedule = Database['public']['Tables']['event_match_schedule']['Row'];

/** How far `markSeatDead` follows dead edges (round 1 → final is at most 7 hops for a
 *  128-draw, plus monrad sub-bracket hops); bounds a malformed progression cycle. */
const DEAD_SEAT_MAX_DEPTH = 12;

/** One seat a completed pool's standings imply, planned before anything is written.
 *  `playerId: null` is a dead seat (no player for that rank). `unresolved` marks a main
 *  seat the single-advancer path could not locate (it reports that itself). */
export interface PlannedSeat {
  stageId: string;
  match: DrawMatch;
  position: 'player1' | 'player2';
  playerId: string | null;
  unresolved?: boolean;
}

/** Players whose seats a pool correction must not touch (see `getSeatGuards`). */
export interface SeatGuards {
  luckyLoserOccupants: Set<string>;
  withdrawn: Set<string>;
}

/**
 * Something the admin must hear about after a result save that otherwise succeeded.
 * Returned up through `processPlayerAdvancement` → `finalizeMatch` → the save routes as an
 * additive `warnings` array. `reseat_blocked`: a corrected pool result changes who
 * advances, but a later match already has a result, so the draw was NOT re-seated at all.
 */
export interface AdvancementWarning {
  code: 'reseat_blocked';
  drawId: string;
  poolNumber: number;
  /** `reason` is prose, except the machine-readable codes `WITHDRAWN_OCCUPANT_REASON` and
   *  `LUCKY_LOSER_OCCUPANT_REASON` (`src/draw/utils/seatPlan.ts`). */
  matches: Array<{ matchId: string; drawMatchId: string | null; stageId: string; reason: string }>;
}

/** How many auto-walkover hops `assertCanRevertMatchResult` will verify before it
 *  refuses. A real bracket cascade is 1-2 hops; the cap bounds the walk (and any
 *  malformed progression cycle) rather than describing a legitimate depth. */
const REVERT_CASCADE_MAX_DEPTH = 5;

/** How many BYE hops a re-seat may follow, one per round: the deepest bracket the draw form
 *  can produce is a 64-draw (e.g. a tier fed by 16 pools × 3), 6 rounds.
 *  `byeCascadeBlocked` verifies at most this many hops and the re-seat's own BYE cascade
 *  (`processPlayerAdvancement`, which starts at depth 1 and throws at `maxDepth`) is given
 *  `BYE_CASCADE_MAX_DEPTH` so it can write exactly as many — the guard never approves a
 *  chain the cascade cannot finish. */
const BYE_CASCADE_MAX_HOPS = 6;
const BYE_CASCADE_MAX_DEPTH = BYE_CASCADE_MAX_HOPS + 1;

/** A downstream `completed` + `outcome = 'walkover'` row is only ever walked
 *  through (and unwound) by the revert path when it is an auto-cascade stamp BY
 *  CONSTRUCTION: no loser was recorded and exactly one seat was ever filled —
 *  the shape that arises precisely because nobody could arrive in the other seat.
 *  An admin-entered walkover (forfeit / convert-to-walkover) always has BOTH
 *  seats filled and a real `loser_id`, and must keep blocking the revert.
 *  `assertCanRevertMatchResult` and `revertMatchResult` share this one predicate
 *  so the guard's verdict and the unwind's action can never drift apart. */
const isCascadeWalkoverArtifact = (row: {
  status?: string | null;
  outcome?: string | null;
  player1_id?: string | null;
  player2_id?: string | null;
  loser_id?: string | null;
}): boolean =>
  row.status === 'completed' &&
  row.outcome === 'walkover' &&
  !row.loser_id &&
  // exactly one seat occupied
  !row.player1_id !== !row.player2_id;

type DrawMatchWithPlayers = DrawMatch & {
  player_1?: Pick<
    Player,
    'id' | 'first_name' | 'last_name' | 'username' | 'alias' | 'current_rating' | 'profile_photo'
  > | null;
  player_2?: Pick<
    Player,
    'id' | 'first_name' | 'last_name' | 'username' | 'alias' | 'current_rating' | 'profile_photo'
  > | null;
  winner?: Pick<
    Player,
    'id' | 'first_name' | 'last_name' | 'username' | 'current_rating' | 'profile_photo'
  > | null;
  loser?: Pick<Player, 'id' | 'first_name' | 'last_name'> | null;
  event_match_schedule?:
    | (Pick<
        EventMatchSchedule,
        | 'id'
        | 'scheduled_datetime'
        | 'court_id'
        | 'estimated_duration_minutes'
        | 'status'
        | 'notes'
        | 'settings'
        | 'best_of'
      > & {
        court_name?: string | null;
        match?: {
          id: string;
          status: string;
          winner_id: string | null;
          player_1_games: number | null;
          player_2_games: number | null;
          standing_points?: unknown;
          /** Additive 2026-10-25 (slim RPC): the format played and the retiree, if any. */
          best_of?: number | null;
          retired_player_id?: string | null;
          match_game?: Array<{
            id: string;
            game_number: number;
            player_1_score: number;
            player_2_score: number;
            /** Additive 2026-10-25 (slim RPC): 'in_progress' marks a retired match's unfinished game. */
            status?: string;
          }> | null;
        } | null;
      })
    | null;
};

/** Sidebar stats for the admin draw list — see drawStats.ts for the roll-ups. */
export interface DrawEventStats {
  entriesByGroup: Record<string, number>;
  matchesByDraw: Record<
    string,
    {
      total: number;
      /** Matches with a result: a winner, or `status = 'completed'`. */
      completed: number;
      scheduledAt: string[];
    }
  >;
}

type CompleteDrawData = {
  draw: DrawWithGroup;
  registrants: GroupRegistrant[];
  stages: DrawStage[];
  rounds: DrawRound[];
  matches: DrawMatchWithPlayers[];
  seeding: DrawSeeding[];
  standings?: StageStandings[];
  progressions?: {
    fromMatchId: string;
    toMatchId: string;
    outcome: 'winner' | 'loser';
    position: 'player1' | 'player2';
  }[];
  /** The event's federation formats names the Japanese way (JPY default currency). */
  isJapanese: boolean;
};

// The draw / division -> event hop for the federation alias rule
// (`alias_allowed_for_event`); a row with no event answers "allowed".
async function isAliasAllowedFor(table: 'draw' | 'event_group', id: string): Promise<boolean> {
  const client = createClient();
  const { data } = await client.from(table).select('event_id').eq('id', id).maybeSingle();
  return data?.event_id ? isEventAliasAllowed(data.event_id, client) : true;
}

export const drawModel = {
  /**
   * Get group registrants for a specific event group
   * @param eventGroupId - The event group ID
   * @returns Array of registrants with player information
   */
  async getGroupRegistrants(eventGroupId: string): Promise<GroupRegistrant[]> {
    const client = createClient();
    if (!client) throw new Error('Supabase client not configured');

    const { data: registrants, error } = await client
      .from('event_group_registration')
      .select(
        `
        *,
        player:player_id (
          id,
          first_name,
          last_name,
          username,
          alias,
          current_rating,
          profile_photo
        )
      `,
      )
      .eq('event_group_id', eventGroupId)
      .order('created_at', { ascending: true });

    if (error) {
      console.error('Error fetching group registrants:', error);
      throw new Error('Failed to fetch group registrants');
    }

    return gateRowAliases(registrants || [], ['player'], () =>
      isAliasAllowedFor('event_group', eventGroupId),
    );
  },

  /**
   * Get draw stages for a specific draw
   * @param drawId - The draw ID
   * @returns Array of draw stages
   */
  async getDrawStages(drawId: string): Promise<DrawStage[]> {
    const client = createClient();
    if (!client) throw new Error('Supabase client not configured');

    const { data: stages, error } = await client
      .from('draw_stage')
      .select('*')
      .eq('draw_id', drawId)
      .order('order', { ascending: true });

    if (error) {
      console.error('Error fetching draw stages:', error);
      throw new Error('Failed to fetch draw stages');
    }

    return stages || [];
  },

  /**
   * Get draw rounds for a specific draw
   * @param drawId - The draw ID
   * @returns Array of draw rounds
   */
  async getDrawMatchProgressions(drawId: string, strict = false) {
    const client = createClient();
    if (!client) throw new Error('Supabase client not configured');

    const { data, error } = await client
      .from('draw_match_progression')
      .select(
        `
        progression_type,
        player_position,
        from_match:draw_match!draw_match_progression_from_match_id_fkey(draw_match_id),
        to_match:draw_match!draw_match_progression_to_match_id_fkey(draw_match_id)
      `,
      )
      .eq('draw_id', drawId);

    if (error) {
      console.error('Error fetching match progressions:', error);
      if (strict) throw new Error('Failed to fetch match progressions');
      return [];
    }

    if (
      strict &&
      (data || []).some(
        (p) =>
          !p.from_match?.draw_match_id ||
          !p.to_match?.draw_match_id ||
          !['winner', 'loser'].includes(p.progression_type) ||
          !['player1', 'player2'].includes(p.player_position),
      )
    ) {
      throw new Error('Invalid match progression');
    }
    return (data || [])
      .filter((p) => p.from_match?.draw_match_id && p.to_match?.draw_match_id)
      .map((p) => ({
        fromMatchId: p.from_match!.draw_match_id!,
        toMatchId: p.to_match!.draw_match_id!,
        outcome: p.progression_type as 'winner' | 'loser',
        position: p.player_position as 'player1' | 'player2',
      }));
  },

  async getDrawRounds(drawId: string): Promise<DrawRound[]> {
    const client = createClient();
    if (!client) throw new Error('Supabase client not configured');

    const { data: rounds, error } = await client
      .from('draw_round')
      .select('*')
      .eq('draw_id', drawId)
      .order('round_number', { ascending: true });

    if (error) {
      console.error('Error fetching draw rounds:', error);
      throw new Error('Failed to fetch draw rounds');
    }

    return rounds || [];
  },

  /**
   * Get draw matches with player information
   * @param drawId - The draw ID (required)
   * @param stageId - The stage ID (optional, if provided filters by stage within the draw)
   * @returns Array of draw matches with player data
   */
  async getDrawMatches(drawId: string, stageId?: string): Promise<DrawMatchWithPlayers[]> {
    const client = createClient();
    if (!client) throw new Error('Supabase client not configured');

    let query = client.from('draw_match').select(
      `
        *,
        player_1:player!player1_id (
          id,
          first_name,
          last_name,
          username,
          alias,
          current_rating,
          profile_photo
        ),
        player_2:player!player2_id (
          id,
          first_name,
          last_name,
          username,
          alias,
          current_rating,
          profile_photo
        ),
        winner:player!winner_id (
          id,
          first_name,
          last_name,
          username,
          current_rating,
          profile_photo
        ),
        loser:player!loser_id (
          id,
          first_name,
          last_name
        ),
        event_match_schedule:event_match_schedule_id (
          id,
          scheduled_datetime,
          court_id,
          estimated_duration_minutes,
          status,
          notes,
          settings,
          best_of,
          match:match_id (
            id,
            status,
            winner_id,
            player_1_games,
            player_2_games,
            standing_points,
            best_of,
            retired_player_id,
            match_game (
              id,
              game_number,
              player_1_score,
              player_2_score,
              status
            )
          )
        )
      `,
    );

    query = query.eq('draw_id', drawId);
    if (stageId) {
      query = query.eq('stage_id', stageId);
    }

    const { data: matches, error } = await query
      .order('round_number', { ascending: true })
      .order('match_number', { ascending: true });

    if (error) {
      const errorMessage = stageId
        ? 'Failed to fetch draw matches by stage'
        : 'Failed to fetch draw matches';
      console.error(errorMessage, error);
      throw new Error(errorMessage);
    }

    return gateRowAliases(matches || [], ['player_1', 'player_2'], () =>
      isAliasAllowedFor('draw', drawId),
    );
  },

  /** Slim draw matches via RPC — strips score replay data, ~76% smaller payload */
  async getDrawMatchesSlim(drawId: string): Promise<DrawMatchWithPlayers[]> {
    const client = createClient();
    if (!client) throw new Error('Supabase client not configured');

    const { data, error } = await client.rpc('get_draw_matches_slim', { p_draw_id: drawId });

    if (error) {
      console.error('Error fetching slim draw matches:', error);
      throw new Error('Failed to fetch draw matches');
    }

    return (data || []) as DrawMatchWithPlayers[];
  },

  /**
   * `best_of` per draw match: the draw match's own and its schedule row's. The slim RPC carries
   * best_of only on the joined match, so an unplayed match would lose its format. Two plain
   * columns, no RPC change. Best effort: on a failed read it logs and returns an empty map.
   */
  async getDrawMatchFormats(
    drawId: string,
  ): Promise<Map<string, { best_of: number | null; event_match_schedule: { best_of: number | null } | null }>> {
    const client = createClient();
    if (!client) throw new Error('Supabase client not configured');

    const { data, error } = await client
      .from('draw_match')
      .select('id, best_of, event_match_schedule:event_match_schedule_id ( best_of )')
      .eq('draw_id', drawId);
    if (error || !data) {
      if (error) console.error('Error fetching draw match formats:', error);
      return new Map();
    }
    return new Map(
      (
        data as unknown as Array<{
          id: string;
          best_of: number | null;
          event_match_schedule: { best_of: number | null } | null;
        }>
      ).map((f) => [f.id, { best_of: f.best_of, event_match_schedule: f.event_match_schedule }]),
    );
  },

  /**
   * Get draw seeding information for a specific draw
   * @param drawId - The draw ID
   * @returns Array of draw seeding data
   */
  async getDrawSeeding(drawId: string): Promise<DrawSeeding[]> {
    const client = createClient();
    if (!client) throw new Error('Supabase client not configured');

    const { data: seeding, error } = await client
      .from('draw_seeding')
      .select('*')
      .eq('draw_id', drawId)
      .order('seed_number', { ascending: true });

    if (error) {
      console.error('Error fetching draw seeding:', error);
      throw new Error('Failed to fetch draw seeding');
    }

    return seeding || [];
  },

  /**
   * Get a single draw stage by ID
   * @param stageId - The stage ID
   * @returns Draw stage data or null if not found
   */
  async getDrawStageById(stageId: string): Promise<DrawStage | null> {
    const client = createClient();
    if (!client) throw new Error('Supabase client not configured');

    const { data: stage, error } = await client
      .from('draw_stage')
      .select('*')
      .eq('id', stageId)
      .single();

    if (error) throw error;

    return stage;
  },

  /**
   * Get a single draw match by ID
   * @param matchId - The match ID
   * @returns Draw match data or null if not found
   */
  async getDrawMatchById(matchId: string): Promise<DrawMatch | null> {
    const client = createClient();
    if (!client) throw new Error('Supabase client not configured');

    const { data: match, error } = await client
      .from('draw_match')
      .select('*')
      .eq('id', matchId)
      .single();

    if (error) throw error;

    return match;
  },

  /**
   * Get draw matches for a specific stage
   * @param stageId - The stage ID
   * @returns Array of draw matches
   */
  async getDrawMatchesByStageId(stageId: string): Promise<DrawMatch[]> {
    const client = createClient();
    if (!client) throw new Error('Supabase client not configured');

    const { data: matches, error } = await client
      .from('draw_match')
      .select('*')
      .eq('stage_id', stageId)
      .order('round_number', { ascending: true })
      .order('match_number', { ascending: true });

    if (error) throw error;

    return matches || [];
  },

  /**
   * Get draw rounds for a specific stage
   * @param stageId - The stage ID
   * @returns Array of draw rounds
   */
  async getDrawRoundsByStageId(stageId: string): Promise<DrawRound[]> {
    const client = createClient();
    if (!client) throw new Error('Supabase client not configured');

    const { data: rounds, error } = await client
      .from('draw_round')
      .select('*')
      .eq('stage_id', stageId)
      .order('round_number', { ascending: true });

    if (error) throw error;

    return rounds || [];
  },

  /**
   * Get draw matches for a specific round
   * @param roundId - The round ID
   * @returns Array of draw matches
   */
  async getDrawMatchesByRoundId(roundId: string): Promise<DrawMatch[]> {
    const client = createClient();
    if (!client) throw new Error('Supabase client not configured');

    const { data: matches, error } = await client
      .from('draw_match')
      .select('*')
      .eq('round_id', roundId)
      .order('match_number', { ascending: true });

    if (error) throw error;

    return matches || [];
  },

  /**
   * Calculates the designated positions for qualifying pools in the main draw.
   * This implements the seeding logic to determine where qualifying pool winners should be placed.
   *
   * @param mainDrawSize - Total size of the main draw
   * @param totalQualifyingPools - Total number of qualifying pools
   * @returns Array of position indices (0-based) where qualifying pools should be placed
   */
  calculateQualifyingPoolPositions(
    mainDrawSize: number,
    totalQualifyingPools: number,
    seededPlayers: number,
    _advancementsPerPool: number,
  ): number[] {
    // Use the imported seeding utilities to get proper tournament positions

    // Get the draw configuration for the main draw size
    const drawConfig = getDrawPositions(mainDrawSize, mainDrawSize);

    // Calculate positions for qualifying pool winners based on tournament seeding rules
    // Qualifying pools should fill the positions that would be occupied by seeds after the seeded players
    const positions: number[] = [];

    for (let i = 0; i < totalQualifyingPools; i++) {
      const qualifyingPoolNumber = i + 1; // Pool numbers start at 1
      const seedNumber = seededPlayers + qualifyingPoolNumber;

      // Find the position for this seed number in the draw configuration
      const positionIndex = drawConfig.positions.indexOf(seedNumber);

      if (positionIndex !== -1 && positionIndex < mainDrawSize) {
        positions.push(positionIndex);
      }
    }

    return positions;
  },

  /**
   * Get the correct main draw position for a qualifying pool winner using existing seeding logic
   * This reuses the draw system's seeding utilities to ensure proper tournament positioning
   *
   * @param qualifyingPoolNumber - The number of the qualifying pool (1-based)
   * @param mainDrawMatches - All matches in the main draw first round
   * @param totalQualifyingPools - Total number of qualifying pools
   * @param seededPlayers - Number of seeded players in main draw
   * @param _advancementsPerPool - Number of players advancing from each pool
   * @returns Object with match ID and player position, or null if no valid position
   */
  getQualifyingPoolTargetPosition(
    qualifyingPoolNumber: number,
    mainDrawMatches: DrawMatch[],
    totalQualifyingPools: number,
    seededPlayers: number,
    _advancementsPerPool: number,
  ): { matchId: string; playerPosition: 'player1' | 'player2' } | null {
    // Validate qualifying pool number
    if (qualifyingPoolNumber < 1 || qualifyingPoolNumber > totalQualifyingPools) {
      console.error(`Invalid qualifying pool number: ${qualifyingPoolNumber}`);
      return null;
    }

    // Use the imported seeding utilities to get proper tournament positions

    // Calculate which seed number this qualifying pool should represent
    // Qualifying pool 1 -> seed 5, Qualifying pool 2 -> seed 6, etc.
    const seedNumber = seededPlayers + qualifyingPoolNumber;

    // Get the tournament position for this seed number
    const drawSize = mainDrawMatches.length * 2;
    const tournamentPosition = getSeedPosition(seedNumber, drawSize);

    if (!tournamentPosition) {
      console.error(
        `Invalid seed number ${seedNumber} for qualifying pool ${qualifyingPoolNumber}`,
      );
      return null;
    }

    // Convert tournament position to match index and player position
    // Tournament position 1-16 maps to:
    // - Match index: Math.floor((position - 1) / 2)
    // - Player position: ((position - 1) % 2) === 0 ? 'player1' : 'player2'
    const matchIndex = Math.floor((tournamentPosition - 1) / 2);
    const playerPosition = (tournamentPosition - 1) % 2 === 0 ? 'player1' : 'player2';

    // Sort matches by position to ensure consistent ordering
    const sortedMatches = mainDrawMatches.sort((a, b) => (a.position || 0) - (b.position || 0));

    // Check if match index is valid
    if (matchIndex >= sortedMatches.length) {
      console.error(
        `Match index ${matchIndex} exceeds main draw size ${sortedMatches.length} for tournament position ${tournamentPosition}`,
      );
      return null;
    }

    const targetMatch = sortedMatches[matchIndex];

    // No "already filled" rejection: a pool re-save re-runs seating, and the caller
    // (advancePlayerToMainDraw → reseatPlayer) decides between a no-op and a re-seat.
    return { matchId: targetMatch.id, playerPosition };
  },

  /**
   * Update a draw match with a player and optionally update the corresponding event match schedule
   * @param client - Supabase client
   * @param targetDrawMatch - The target draw match to update
   * @param playerId - The player ID to assign
   * @param playerPosition - The position to assign the player ('player1' or 'player2')
   * @param _context - Optional context string for logging (e.g., "progression", "qualifying pool")
   * @returns Promise<void>
   */
  async updateMatchWithPlayer(
    client: ReturnType<typeof createClient>,
    targetDrawMatch: DrawMatch,
    playerId: string,
    playerPosition: 'player1' | 'player2',
    _context?: string,
  ): Promise<void> {
    const playerField = playerPosition === 'player1' ? 'player1_id' : 'player2_id';
    const eventPlayerField = playerPosition === 'player1' ? 'player_1_id' : 'player_2_id';

    // Update the draw_match.
    // `status: 'ready'` is written for every UNDECIDED target, exactly as before — but
    // never for one that is already `completed`. The seat write and the result stamp are
    // two separate writes: downgrading a decided row here would leave, between them,
    // `ready` + a leftover `outcome`/`winner_id` — a shape `isCascadeWalkoverArtifact`
    // does not match, so a crash in the gap would leave the Task 6 revert guard
    // under-blocking and `revertMatchResult` half-resetting a stamped row. Preserving the
    // status keeps every intermediate state either a valid artifact or an ordinary
    // undecided row. Reverts clear the status themselves, so nothing depends on the
    // downgrade.
    const seatUpdate = {
      [playerField]: playerId,
      ...(targetDrawMatch.status === 'completed' ? {} : { status: 'ready' }),
      updated_at: new Date().toISOString(),
    };
    const { error: updateDrawMatchError } = await client
      .from('draw_match')
      .update(seatUpdate)
      .eq('id', targetDrawMatch.id);

    if (updateDrawMatchError) {
      console.error('Error updating target draw match:', updateDrawMatchError);
      throw new Error('Failed to update target draw match');
    }

    // Update the corresponding event_match_schedule if it exists
    if (targetDrawMatch?.event_match_schedule_id) {
      const { error: updateEventScheduleError } = await client
        .from('event_match_schedule')
        .update({
          [eventPlayerField]: playerId,
          updated_at: new Date().toISOString(),
        })
        .eq('id', targetDrawMatch.event_match_schedule_id);

      if (updateEventScheduleError) {
        console.error('Error updating event match schedule:', updateEventScheduleError);
        throw new Error('Failed to update event match schedule');
      }
    }
  },

  /**
   * The single place that writes a match result. Stamps `outcome` (+ winner/loser/score/status)
   * on draw_match and event_match_schedule. Never writes `is_walkover` — the mirror trigger
   * (20260818070000) derives it from `outcome`. Does NOT touch `match_id` or delete `match`
   * rows; callers keep their own match-row create/delete logic and match_id handling.
   */
  async stampMatchOutcome(opts: {
    drawMatchId: string | null;
    scheduleId: string | null;
    outcome: MatchOutcome;
    winnerId: string | null;
    loserId: string | null;
    score?: unknown;
  }): Promise<void> {
    const winnerlessOutcome = opts.outcome === 'dual_withdrawal' || opts.outcome === 'not_played';
    if (winnerlessOutcome && opts.winnerId) {
      throw new Error(`outcome '${opts.outcome}' cannot carry a winner`);
    }
    if (!winnerlessOutcome && !opts.winnerId) {
      throw new Error(`outcome '${opts.outcome}' requires a winner`);
    }
    const client = getSupabaseAdminClient();
    if (!client) throw new Error('Supabase client not configured');

    if (opts.drawMatchId) {
      const { error } = await client
        .from('draw_match')
        .update({
          winner_id: opts.winnerId,
          loser_id: opts.loserId,
          status: 'completed',
          outcome: opts.outcome,
          score: (opts.score ?? null) as never,
          updated_at: new Date().toISOString(),
        })
        .eq('id', opts.drawMatchId);
      if (error) throw error;
    }
    if (opts.scheduleId) {
      const { error } = await client
        .from('event_match_schedule')
        .update({
          status: 'completed',
          outcome: opts.outcome,
          updated_at: new Date().toISOString(),
        })
        .eq('id', opts.scheduleId);
      if (error) throw error;
    }
  },

  /**
   * Mark a placement match with no opponent (the odd-count orphan) as a completed bye:
   * the sole player auto-wins their (last) position. Re-reads the row so player1_id —
   * just written by updateMatchWithPlayer — is current.
   */
  async finalizeByeMatch(
    client: ReturnType<typeof createClient>,
    byeMatch: DrawMatch,
  ): Promise<void> {
    const { data: row, error: readError } = await client
      .from('draw_match')
      .select('player1_id')
      .eq('id', byeMatch.id)
      .single();
    if (readError || !row?.player1_id) {
      console.error('Pool play-offs: cannot finalise bye match without player1', readError);
      return;
    }
    const { error } = await client
      .from('draw_match')
      .update({
        winner_id: row.player1_id,
        is_bye: true,
        status: 'completed',
        updated_at: new Date().toISOString(),
      })
      .eq('id', byeMatch.id);
    if (error) {
      console.error('Pool play-offs: failed to finalise bye match', error);
      throw new Error('Failed to finalise bye match');
    }
  },

  /**
   * Validate if a stage is a round robin qualifying pool
   * @param stage - The draw stage to validate
   * @returns true if the stage is a round robin qualifying pool, false otherwise
   */
  validateRoundRobinStage(stage: DrawStage): boolean {
    return stage.type === 'round_robin' && stage.context === 'qualifying';
  },

  /**
   * Get qualifying pool configuration from draw settings
   * @param client - Supabase client
   * @param drawId - The draw ID
   * @returns Qualifying pool configuration
   */
  async getQualifyingPoolConfiguration(
    client: ReturnType<typeof createClient>,
    drawId: string,
  ): Promise<{
    totalQualifyingPools: number;
    seededPlayers: number;
    advancementsPerPool: number;
  }> {
    // Get draw configuration to determine qualifying pools and seeded players
    const { data: drawData, error: drawError } = await client
      .from('draw')
      .select('event_id, settings')
      .eq('id', drawId)
      .single();

    if (drawError || !drawData?.settings) {
      console.error('Error: Draw settings not found', drawError);
      throw new Error('Failed to fetch draw settings');
    }

    const drawSettings = drawData.settings as {
      options?: {
        qualifyingPools?: { numPools?: number; advancementPerPool?: number };
        seededPlayersCount?: number;
      };
    } | null;
    const drawOptions = drawSettings?.options || {};

    // Get qualifying pools configuration from draw settings
    const totalQualifyingPools = drawOptions.qualifyingPools?.numPools || 0;
    const seededPlayers = drawOptions.seededPlayersCount || 0;
    const advancementsPerPool = drawOptions.qualifyingPools?.advancementPerPool || 1;

    return {
      totalQualifyingPools,
      seededPlayers,
      advancementsPerPool,
    };
  },

  /**
   * Extract pool number from stage ID
   * @param stageId - The stage ID (e.g., "qualifying_pool_1")
   * @returns The pool number (defaults to 1 if not found)
   */
  extractPoolNumberFromStageId(stageId: string): number {
    const poolNumberMatch = stageId.match(/pool_(\d+)/);
    return poolNumberMatch ? parseInt(poolNumberMatch[1]) : 1;
  },

  /**
   * Validate if all matches in a stage are completed
   * @param matches - Array of draw matches to validate
   * @returns true if all matches are completed, false otherwise
   */
  validateAllMatchesCompleted(matches: DrawMatch[]): boolean {
    return matches.every((match) => match.status === 'completed');
  },

  /**
   * Find the main draw stage for a given draw
   * @param drawId - The draw ID
   * @returns The main draw stage or null if not found
   */
  async findMainDrawStage(drawId: string): Promise<DrawStage | null> {
    const allStages = await this.getDrawStages(drawId);
    const mainDrawStage = allStages.find(
      (stage) => stage.context === 'main' && stage.type === 'elimination',
    );

    if (!mainDrawStage) {
      console.error('Error finding main draw stage');
      return null;
    }

    return mainDrawStage;
  },

  /**
   * Get all matches from the first round of the main draw
   * @param mainDrawStage - The main draw stage
   * @returns Array of matches from the first round
   */
  async getMainDrawFirstRoundMatches(mainDrawStage: DrawStage): Promise<DrawMatch[]> {
    // Find the first round of the main draw
    const mainDrawRounds = await this.getDrawRoundsByStageId(mainDrawStage.id);

    if (mainDrawRounds.length === 0) {
      console.error('Error finding main draw rounds');
      throw new Error('No main draw rounds found');
    }

    const firstRound = mainDrawRounds[0];

    // Get all matches in the first round to determine correct positions
    const allRoundMatches = await this.getDrawMatchesByRoundId(firstRound.id);

    if (allRoundMatches.length === 0) {
      console.error('Error finding main draw matches');
      throw new Error('No main draw matches found');
    }

    return allRoundMatches;
  },

  /**
   * Advance a player from qualifying pool to main draw
   * @param client - Supabase client
   * @param drawId - The draw ID
   * @param player - The player to advance
   * @param qualifyingPoolNumber - The qualifying pool number
   * @param allRoundMatches - All matches in the main draw first round
   * @param totalQualifyingPools - Total number of qualifying pools
   * @param seededPlayers - Number of seeded players
   * @param advancementsPerPool - Number of players advancing per pool
   * @returns Promise<void>
   */
  async advancePlayerToMainDraw(
    client: ReturnType<typeof createClient>,
    drawId: string,
    player: { playerId: string },
    qualifyingPoolNumber: number,
    allRoundMatches: DrawMatch[],
    totalQualifyingPools: number,
    seededPlayers: number,
    advancementsPerPool: number,
  ): Promise<void> {
    // Use the proper seeding logic to determine the correct position
    const targetPosition = this.getQualifyingPoolTargetPosition(
      qualifyingPoolNumber,
      allRoundMatches,
      totalQualifyingPools,
      seededPlayers,
      advancementsPerPool,
    );

    if (!targetPosition) {
      console.error(`Error determining position for qualifying pool ${qualifyingPoolNumber}`);
      throw new Error(`Failed to determine position for qualifying pool ${qualifyingPoolNumber}`);
    }

    const targetMatch = allRoundMatches.find((match) => match.id === targetPosition.matchId);
    if (!targetMatch) {
      console.error(`Error finding target match ${targetPosition.matchId}`);
      throw new Error(`Failed to find target match ${targetPosition.matchId}`);
    }

    // Idempotent seat write: no-op when already seated; clear-and-reopen when the
    // occupant changed after a corrected pool result (was: throw "already filled").
    const wrote = await this.reseatPlayer(
      client,
      targetMatch,
      player.playerId,
      targetPosition.playerPosition,
      'qualifying pool',
    );
    if (!wrote) return;

    // Check if the target match now has a BYE and needs further advancement
    const updatedTargetMatch = await this.getDrawMatchById(targetMatch.id);
    if (updatedTargetMatch && this.isMatchWithBye(updatedTargetMatch)) {
      // Recursively call processPlayerAdvancement for the BYE scenario
      // The player who was just placed "wins" the BYE and advances further
      await this.processPlayerAdvancement(
        drawId,
        targetMatch.id,
        player.playerId, // The player who was just placed "wins" the BYE
        '', // No loser in BYE scenario
        1, // Start at depth 1 since this is a recursive call
        BYE_CASCADE_MAX_DEPTH,
      );
    } else if (updatedTargetMatch) {
      // No BYE — but the other seat may be permanently empty because its feeder
      // resolved winner-less. If so, this match is already decided.
      await this.maybeResolveWinnerlessOpponent(client, updatedTargetMatch);
    }
  },

  /**
   * Advance a player to the main draw using a pre-calculated placement
   * @param client - Supabase client
   * @param drawId - Draw ID
   * @param player - Player to advance
   * @param placement - Pre-calculated placement
   * @param allRoundMatches - All matches in the first round of main draw
   * @returns Promise<void>
   */
  async advancePlayerToMainDrawWithPlacement(
    client: ReturnType<typeof createClient>,
    drawId: string,
    player: { playerId: string },
    placement: AdvancementPlacement,
    allRoundMatches: DrawMatch[],
  ): Promise<void> {
    // Find the target match using the pre-calculated match index
    const targetMatch = allRoundMatches[placement.matchIndex];
    if (!targetMatch) {
      console.error(`Error finding target match at index ${placement.matchIndex}`);
      throw new Error(`Failed to find target match at index ${placement.matchIndex}`);
    }

    // Idempotent seat write: no-op when already seated; clear-and-reopen when the
    // occupant changed after a corrected pool result (was: throw "already filled").
    const wrote = await this.reseatPlayer(
      client,
      targetMatch,
      player.playerId,
      placement.playerPosition,
      'qualifying pool',
    );
    if (!wrote) return;

    // Check if the target match now has a BYE and needs further advancement
    const updatedTargetMatch = await this.getDrawMatchById(targetMatch.id);
    if (updatedTargetMatch && this.isMatchWithBye(updatedTargetMatch)) {
      // Recursively call processPlayerAdvancement for the BYE scenario
      // The player who was just placed "wins" the BYE and advances further
      await this.processPlayerAdvancement(
        drawId,
        targetMatch.id,
        player.playerId, // The player who was just placed "wins" the BYE
        '', // No loser in BYE scenario
        1, // Start at depth 1 since this is a recursive call
        BYE_CASCADE_MAX_DEPTH,
      );
    } else if (updatedTargetMatch) {
      // No BYE — but the other seat may be permanently empty because its feeder
      // resolved winner-less. If so, this match is already decided.
      await this.maybeResolveWinnerlessOpponent(client, updatedTargetMatch);
    }
  },

  /**
   * Check if a match contains a BYE player
   * @param match - The draw match to check
   * @returns true if the match contains a BYE player
   */
  isMatchWithBye(match: DrawMatch): boolean {
    return Boolean(match.player1_is_bye === true || match.player2_is_bye === true);
  },

  /**
   * Get the player position that is a BYE
   * @param match - The draw match to check
   * @returns 'player1' or 'player2' if a BYE exists, null otherwise
   */
  getByePlayerPosition(match: DrawMatch): 'player1' | 'player2' | null {
    if (match.player1_is_bye === true) return 'player1';
    if (match.player2_is_bye === true) return 'player2';
    return null;
  },

  /**
   * Decide whether ONE seat of `targetMatch` can NEVER be filled because its feeder
   * resolved winner-less (`dual_withdrawal` / `not_played`): if so, resolve
   * `targetMatch` as a walkover win for the player already seated in the other seat,
   * and cascade onwards. The winner-less counterpart of the `isMatchWithBye()`
   * recursion.
   *
   * Called from FOUR places, because the two feeders can resolve in either order:
   * the three post-seat sites (a player ARRIVES into a match whose sibling feeder
   * was already winner-less) and the dead-edge branch of `processPlayerAdvancement`
   * (the winner-less stamp lands SECOND, on a match where the opponent is already
   * waiting and nobody new arrives to trigger the check). Order must not matter.
   *
   * When BOTH feeders are winner-less the target has no seated player at all: there
   * is nobody to walk over, so it stays empty and unplayable rather than being
   * stamped. That is the intended end state.
   *
   * Deliberately NOT `isWinnerless()`: that predicate also covers `walkover`, which
   * DOES produce a winner — a walkover feeder still sends its winner into a
   * winner-edge seat, and an admin walkover (real loser recorded) sends that loser
   * on too. The one walkover edge that IS dead is the LOSER edge of a loser-less
   * walkover — the shape of the cascade's own artifact — because that match, like
   * its winner-less feeder, produced no loser.
   *
   * The stamp is shaped so the revert path can undo it: winner set, NO loser,
   * exactly one seat occupied — `isCascadeWalkoverArtifact`. Do not add a loser and
   * do not fill the dead seat, or `revertMatchResult` stops unwinding this row.
   *
   * `depth`/`maxDepth` ride along with the BYE recursion's budget: a malformed
   * (cyclic) progression graph must not cascade forever, and the chain it creates
   * has to stay inside what the revert walk can unwind.
   *
   * @returns true when it resolved the match.
   */
  async maybeResolveWinnerlessOpponent(
    client: ReturnType<typeof createClient>,
    targetMatch: DrawMatch,
    depth: number = 0,
    maxDepth: number = 3,
  ): Promise<boolean> {
    const seated = targetMatch.player1_id ?? targetMatch.player2_id;
    const emptyPos = targetMatch.player1_id ? 'player2' : 'player1';
    if (!seated || (targetMatch.player1_id && targetMatch.player2_id)) return false;
    if (targetMatch.status === 'completed') return false; // already decided — re-runs are no-ops
    if (this.isMatchWithBye(targetMatch)) return false; // the BYE path owns those

    // Not filtered by progression_type — a 'loser' edge out of a winner-less
    // feeder is just as dead — but the edge type is FETCHED: it decides whether a
    // loser-less walkover feeder (the cascade's own artifact) counts as dead below.
    const { data: feeders, error } = await client
      .from('draw_match_progression')
      .select('from_match_id, player_position, progression_type')
      .eq('to_match_id', targetMatch.id);
    if (error || !feeders) return false; // advancement is best-effort; the guard is elsewhere

    const emptyFeeder = feeders.find((f) => f.player_position === emptyPos);
    if (!emptyFeeder) return false; // nothing feeds that seat — it was seeded/placed directly

    const { data: feederMatch } = await client
      .from('draw_match')
      .select('id, status, outcome, loser_id, player1_id, player2_id')
      .eq('id', emptyFeeder.from_match_id)
      .single();
    const feederIsDeadForThisEdge =
      feederMatch?.status === 'completed' &&
      (feederMatch.outcome === 'dual_withdrawal' ||
        feederMatch.outcome === 'not_played' ||
        // The cascade's own artifact — the SAME predicate the revert walk trusts
        // (loser-less, exactly-one-seat walkover) — produced no loser, so its loser
        // edge can never deliver. A walkover with a recorded loser (admin walkover)
        // still sends that loser on; a walkover's winner edge always delivers its
        // winner; and a both-seats walkover merely MISSING its loser record had a
        // real opponent — all three stay alive.
        (emptyFeeder.progression_type === 'loser' && isCascadeWalkoverArtifact(feederMatch)));
    if (!feederIsDeadForThisEdge) return false;

    // Budget guard: stamping commits us to advancing the winner — one more
    // `processPlayerAdvancement` hop at depth + 1, which THROWS at maxDepth. Every
    // artifact this helper stamps is itself a loser-less walkover whose dead loser
    // edge re-triggers this helper, so Monrad-style consolation chains recurse one
    // hop per round. Refusing BEFORE the stamp turns depth exhaustion into a
    // logged, stranded seat (the pre-existing worst case) instead of a 500 thrown
    // over a stamped-but-unadvanced artifact. Placed AFTER the dead-feeder check
    // so the log fires only for a seat that genuinely strands.
    if (depth + 1 >= maxDepth) {
      console.error(
        `[maybeResolveWinnerlessOpponent] cascade budget exhausted at depth ${depth} for match ${targetMatch.id} — seat left unresolved`,
      );
      return false;
    }

    await this.stampMatchOutcome({
      drawMatchId: targetMatch.id,
      scheduleId: targetMatch.event_match_schedule_id ?? null,
      outcome: 'walkover',
      winnerId: seated,
      loserId: null,
    });
    await this.processPlayerAdvancement(
      targetMatch.draw_id,
      targetMatch.id,
      seated,
      '', // nobody lost this one either — the loser edge stays empty
      depth + 1,
      maxDepth,
    );
    return true;
  },

  /**
   * Check if a match is ready for advancement (has one real player and one BYE)
   * @param match - The draw match to check
   * @returns true if the match has one real player and one BYE
   */
  isMatchReadyForAdvancement(match: DrawMatch): boolean {
    const hasBye = this.isMatchWithBye(match);
    const hasRealPlayer = Boolean(
      (match.player1_id && match.player1_is_bye !== true) ||
        (match.player2_id && match.player2_is_bye !== true),
    );
    return hasBye && hasRealPlayer;
  },

  /**
   * Get complete draw data including all related information
   * @param drawId - The draw ID
   * @param eventId - The event ID (for validation)
   * @param event - Optional event data to avoid duplicate loading
   * @returns Complete draw data with all related information
   */
  async getCompleteDrawData(
    drawId: string,
    eventId: string,
    event?: {
      id: string;
      federation_id: string;
      location_id: string | null;
      settings: unknown;
    } | null,
    options?: { slim?: boolean; strictProgressions?: boolean; matchFormats?: boolean },
  ): Promise<CompleteDrawData> {
    // Get the draw with group information
    const draw = await this.getDrawById(drawId, eventId);
    if (!draw) {
      throw new Error('Draw not found');
    }

    // Fetch all related data in parallel
    // slim mode uses RPC that strips score replay data (~76% smaller)
    const [registrants, stages, rounds, rawMatches, seeding, eventData, progressions, formats] =
      await Promise.all([
        this.getGroupRegistrants(draw.event_group_id),
        this.getDrawStages(drawId),
        this.getDrawRounds(drawId),
        options?.slim ? this.getDrawMatchesSlim(drawId) : this.getDrawMatches(drawId),
        this.getDrawSeeding(drawId),
        event || eventModelV2.getEvent(eventId),
        this.getDrawMatchProgressions(drawId, options?.strictProgressions),
        options?.slim && options.matchFormats ? this.getDrawMatchFormats(drawId) : null,
      ]);
    const matches = formats
      ? rawMatches.map((row) => {
          const f = formats.get(row.id);
          if (!f) return row;
          return {
            ...row,
            best_of: f.best_of ?? row.best_of ?? null,
            event_match_schedule: row.event_match_schedule
              ? { ...row.event_match_schedule, best_of: f.event_match_schedule?.best_of ?? null }
              : row.event_match_schedule,
          };
        })
      : rawMatches;

    // Fetch federation currency for Japanese name formatting
    let isJapanese = false;
    if (eventData?.federation_id) {
      const { data: fed } = await createClient()
        .from('federation')
        .select('default_currency')
        .eq('id', eventData.federation_id)
        .maybeSingle();
      isJapanese = fed?.default_currency === 'jpy';
    }

    // Resolve court names for matches with event_match_schedule — once per
    // event, labelled by venue when the event has more than one.
    const venueCtx =
      eventData?.settings && matches.some((m) => m.event_match_schedule?.court_id)
        ? await loadVenueContextForEvent(createClient(), {
            id: eventData.id,
            location_id: eventData.location_id,
            settings: eventData.settings,
          })
        : null;
    const matchesWithCourtNames = matches.map((match) => {
      if (match.event_match_schedule?.court_id && venueCtx) {
        const courtName = courtLabel(venueCtx, match.event_match_schedule.court_id);
        return {
          ...match,
          event_match_schedule: {
            ...match.event_match_schedule,
            court_name: courtName,
          },
        };
      }
      return match;
    });

    // Calculate standings for round robin stages
    const standings = this.calculateStandingsForDraw({
      draw,
      stages,
      rounds,
      matches: matchesWithCourtNames,
      isJapanese,
    });

    return {
      draw,
      registrants,
      stages,
      rounds,
      matches: matchesWithCourtNames,
      seeding,
      standings,
      progressions,
      isJapanese,
    };
  },

  /**
   * Calculate standings for round robin stages in a draw
   * @param params - Object containing draw data needed for standings calculation
   * @returns Array of stage standings or undefined if no round robin stages
   */
  calculateStandingsForDraw(params: {
    draw: DrawWithGroup;
    stages: DrawStage[];
    rounds: DrawRound[];
    matches: DrawMatchWithPlayers[];
    isJapanese?: boolean;
  }): StageStandings[] | undefined {
    const { draw, stages, rounds, matches, isJapanese } = params;

    // Check if there are any round robin stages
    const hasRoundRobinStages = stages.some((stage) => stage.type === 'round_robin');

    if (!hasRoundRobinStages) {
      return undefined;
    }

    // Each stage under its own block: a pool by "Pool scoring", a round-robin main draw by the
    // Main draw section's; a futsal-show ladder is always ranked the futsal way.
    const drawSettings = draw.settings as Record<string, unknown> | null;
    const drawOptions = ((drawSettings?.options as DrawOptions | undefined) || {}) as DrawOptions;
    const isFutsalShowLadder =
      (drawOptions.ladderTemplate as { id?: string } | undefined)?.id === 'futsal-show-ladder';

    return calculateStandingsForDraw({
      stages,
      rounds,
      matches,
      isJapanese,
      settingsForStage: (stage) => {
        const settings = resolveRoundRobinSettings(drawOptions, stage);
        return {
          scoringSystem: isFutsalShowLadder
            ? 'futsal_show'
            : (settings?.scoringSystem ?? 'standard'),
          tiebreakerOrder: settings?.tiebreakerOrder ?? ['games_ratio', 'points_ratio'],
        };
      },
    });
  },

  /**
   * Get all draws for an event with event group information
   * @param eventId - The event ID
   * @returns Array of draws with event group data
   */
  async getDrawsForEvent(eventId: string): Promise<DrawWithGroup[]> {
    const client = createClient();
    if (!client) throw new Error('Supabase client not configured');

    const { data: draws, error } = await client
      .from('draw')
      .select(
        `
        *,
        event_group:event_group_id (
          id,
          name,
          group_type,
          max_participants,
          sort_order,
          is_team
        )
      `,
      )
      .eq('event_id', eventId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching draws:', error);
      throw new Error('Failed to fetch draws');
    }

    return draws || [];
  },

  /**
   * Entry + match counts for the admin draw list sidebar.
   * Entries = ACTIVE `event_group_registration` rows per group (withdrawn rows
   * are excluded, matching the entrants RPC and the qualifying-pool loader);
   * matches = non-bye `draw_match` rows per draw with each scheduled match's
   * datetime (day bucketing happens in the browser, per the reports timezone
   * rule). Both reads are paged in 1000-row chunks so PostgREST `max_rows`
   * never silently truncates a large event; termination waits for an empty
   * page rather than assuming the deployed `max_rows`.
   */
  async getDrawStatsForEvent(eventId: string): Promise<DrawEventStats> {
    const client = createClient();
    if (!client) throw new Error('Supabase client not configured');

    const PAGE_SIZE = 1000;
    const pageAll = async <T>(
      label: string,
      query: (from: number, to: number) => PromiseLike<{ data: T[] | null; error: unknown }>,
    ): Promise<T[]> => {
      const rows: T[] = [];
      let from = 0;
      for (;;) {
        const { data, error } = await query(from, from + PAGE_SIZE - 1);
        if (error) {
          console.error(`Error fetching draw stats (${label}):`, error);
          throw new Error('Failed to fetch draw stats');
        }
        const page = data ?? [];
        rows.push(...page);
        if (page.length < PAGE_SIZE) break;
        from += page.length;
      }
      return rows;
    };

    const [regs, matches] = await Promise.all([
      pageAll('entries', (from, to) =>
        client
          .from('event_group_registration')
          .select('event_group_id, event_group!inner(event_id)')
          .eq('event_group.event_id', eventId)
          .eq('status', 'active')
          .order('id')
          .range(from, to),
      ),
      pageAll('matches', (from, to) =>
        client
          .from('draw_match')
          .select(
            'draw_id, winner_id, status, event_match_schedule(scheduled_datetime), draw!inner(event_id)',
          )
          .eq('draw.event_id', eventId)
          .not('is_bye', 'is', true)
          .order('id')
          .range(from, to),
      ),
    ]);

    const entriesByGroup: Record<string, number> = {};
    for (const row of regs) {
      entriesByGroup[row.event_group_id] = (entriesByGroup[row.event_group_id] ?? 0) + 1;
    }

    const matchesByDraw: DrawEventStats['matchesByDraw'] = {};
    for (const row of matches) {
      const bucket = (matchesByDraw[row.draw_id] ??= { total: 0, completed: 0, scheduledAt: [] });
      bucket.total += 1;
      if (row.winner_id || row.status === 'completed') bucket.completed += 1;
      const scheduledAt = row.event_match_schedule?.scheduled_datetime;
      if (scheduledAt) bucket.scheduledAt.push(scheduledAt);
    }

    return { entriesByGroup, matchesByDraw };
  },

  /** Lightweight draw list for public pages: of `settings`, only the template and pool plan. */
  async getDrawListForEvent(eventId: string): Promise<DrawListItem[]> {
    const client = createClient();
    if (!client) throw new Error('Supabase client not configured');

    const { data: draws, error } = await client
      .from('draw')
      .select(
        `
        id,
        name,
        status,
        draw_type,
        draw_size,
        num_rounds,
        format_type,
        template:settings->>template,
        qualifying_pools:settings->options->qualifyingPools,
        event_id,
        event_group_id,
        created_at,
        updated_at,
        hidden_from_public,
        event_group:event_group_id (
          id,
          name,
          group_type,
          max_participants,
          sort_order,
          is_team
        )
      `,
      )
      .eq('event_id', eventId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching draw list:', error);
      throw new Error('Failed to fetch draws');
    }

    return (draws || []) as DrawListItem[];
  },

  /**
   * Get a single draw by ID with event group information
   * @param drawId - The draw ID
   * @param eventId - The event ID (for validation)
   * @returns Draw with event group data or null if not found
   */
  async getDrawById(drawId: string, eventId: string): Promise<DrawWithGroup | null> {
    const client = createClient();
    if (!client) throw new Error('Supabase client not configured');

    const { data: draw, error } = await client
      .from('draw')
      .select(
        `
        *,
        event_group:event_group_id (
          id,
          name,
          group_type,
          max_participants,
          sort_order,
          is_team
        )
      `,
      )
      .eq('id', drawId)
      .eq('event_id', eventId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null; // No rows returned
      console.error('Error fetching draw:', error);
      throw new Error('Failed to fetch draw');
    }

    return draw;
  },

  /**
   * Get draw settings by draw ID
   * @param drawId - The draw ID
   * @returns Draw settings or null if not found
   */
  async getDrawSettings(drawId: string): Promise<Record<string, unknown> | null> {
    const client = createClient();
    if (!client) throw new Error('Supabase client not configured');

    const { data: drawData, error } = await client
      .from('draw')
      .select('settings')
      .eq('id', drawId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null; // No rows returned
      console.error('Error fetching draw settings:', error);
      throw new Error('Failed to fetch draw settings');
    }

    return (drawData?.settings as Record<string, unknown> | null) || null;
  },

  /**
   * Create a new draw
   * @param drawData - The draw data
   * @param isAdmin - Whether to use admin client
   * @returns The created draw with event group data
   */
  async createDraw(
    drawData: Omit<DrawInsert, 'id' | 'created_at' | 'updated_at'>,
    isAdmin: boolean = false,
  ): Promise<DrawWithGroup> {
    const client = isAdmin ? getSupabaseAdminClient() : createClient();
    if (!client) throw new Error('Supabase client not configured');

    // Validate required fields
    if (
      !drawData.event_group_id ||
      !drawData.name ||
      !drawData.format_type ||
      !drawData.draw_size
    ) {
      throw new Error('Missing required fields');
    }

    // Check if event group exists and belongs to the event
    const { data: eventGroup, error: groupError } = await client
      .from('event_group')
      .select('id, name')
      .eq('id', drawData.event_group_id)
      .eq('event_id', drawData.event_id)
      .single();

    if (groupError || !eventGroup) {
      throw new Error('Invalid event group');
    }

    // Check for duplicate draw type in the same group
    const { data: existingDraw } = await client
      .from('draw')
      .select('id')
      .eq('event_group_id', drawData.event_group_id)
      .eq('draw_type', drawData.draw_type!)
      .single();

    if (existingDraw) {
      throw new Error(`A ${drawData.draw_type} draw already exists for this group`);
    }

    const { data: draw, error } = await client
      .from('draw')
      .insert(drawData)
      .select(
        `
        *,
        event_group:event_group_id (
          id,
          name,
          group_type,
          max_participants,
          sort_order,
          is_team
        )
      `,
      )
      .single();

    if (error) {
      console.error('Error creating draw:', error);
      throw new Error('Failed to create draw');
    }

    return draw;
  },

  /**
   * Update an existing draw
   * @param drawId - The draw ID
   * @param eventId - The event ID (for validation)
   * @param updates - The updates to apply
   * @param isAdmin - Whether to use admin client
   * @returns The updated draw with event group data
   */
  async updateDraw(
    drawId: string,
    eventId: string,
    updates: Omit<DrawUpdate, 'id' | 'created_at' | 'updated_at'>,
    isAdmin: boolean = false,
  ): Promise<DrawWithGroup> {
    const client = isAdmin ? getSupabaseAdminClient() : createClient();
    if (!client) throw new Error('Supabase client not configured');

    // Check if draw exists
    const { data: existingDraw, error: existingError } = await client
      .from('draw')
      .select('id, event_group_id, draw_type')
      .eq('id', drawId)
      .eq('event_id', eventId)
      .single();

    if (existingError || !existingDraw) {
      throw new Error('Draw not found');
    }

    // If updating draw_type or event_group_id, check for duplicates
    if (updates.draw_type || updates.event_group_id) {
      const newDrawType = updates.draw_type || existingDraw.draw_type;
      const newEventGroupId = updates.event_group_id || existingDraw.event_group_id;

      const { data: duplicateDraw } = await client
        .from('draw')
        .select('id')
        .eq('event_group_id', newEventGroupId)
        .eq('draw_type', newDrawType)
        .neq('id', drawId)
        .single();

      if (duplicateDraw) {
        throw new Error(`A ${newDrawType} draw already exists for this group`);
      }
    }

    // Remove undefined values
    const cleanUpdates = Object.fromEntries(
      Object.entries(updates).filter(([_, value]) => value !== undefined),
    );

    const { data: draw, error } = await client
      .from('draw')
      .update(cleanUpdates)
      .eq('id', drawId)
      .select(
        `
        *,
        event_group:event_group_id (
          id,
          name,
          group_type,
          max_participants,
          sort_order,
          is_team
        )
      `,
      )
      .single();

    if (error) {
      console.error('Error updating draw:', error);
      throw new Error('Failed to update draw');
    }

    return draw;
  },

  /**
   * Delete a draw
   * @param drawId - The draw ID
   * @param eventId - The event ID (for validation)
   * @param isAdmin - Whether to use admin client
   */
  async deleteDraw(drawId: string, eventId: string, isAdmin: boolean = false): Promise<void> {
    const client = isAdmin ? getSupabaseAdminClient() : createClient();
    if (!client) throw new Error('Supabase client not configured');

    // Check if draw exists and get its status
    const { data: existingDraw, error: existingError } = await client
      .from('draw')
      .select('id, status')
      .eq('id', drawId)
      .eq('event_id', eventId)
      .single();

    if (existingError || !existingDraw) {
      throw new Error('Draw not found');
    }

    const { error } = await client.from('draw').delete().eq('id', drawId);

    if (error) {
      console.error('Error deleting draw:', error);
      throw new Error('Failed to delete draw');
    }
  },

  /**
   * Check if a draw type already exists for a group
   * @param eventGroupId - The event group ID
   * @param drawType - The draw type
   * @param excludeDrawId - Optional draw ID to exclude from check (for updates)
   * @returns True if draw type already exists
   */
  async checkDrawTypeExists(
    eventGroupId: string,
    drawType: string,
    excludeDrawId?: string,
  ): Promise<boolean> {
    const client = createClient();
    if (!client) throw new Error('Supabase client not configured');

    let query = client
      .from('draw')
      .select('id')
      .eq('event_group_id', eventGroupId)
      .eq('draw_type', drawType);

    if (excludeDrawId) {
      query = query.neq('id', excludeDrawId);
    }

    const { data, error } = await query.single();

    if (error && error.code !== 'PGRST116') {
      console.error('Error checking draw type:', error);
      throw new Error('Failed to check draw type');
    }

    return !!data;
  },

  /**
   * Get draw match by event match schedule ID
   * @param eventMatchScheduleId - The event match schedule ID
   * @param isAdmin - Whether to use admin client
   */
  async getByEventScheduleId(
    eventMatchScheduleId: string,
    isAdmin: boolean = false,
  ): Promise<Database['public']['Tables']['draw_match']['Row'] | null> {
    const client = isAdmin ? getSupabaseAdminClient() : createClient();
    if (!client) throw new Error('Supabase client not configured');

    const { data: drawMatch, error: drawMatchError } = await client
      .from('draw_match')
      .select('*')
      .eq('event_match_schedule_id', eventMatchScheduleId)
      .single();

    if (drawMatchError) {
      if (drawMatchError.code === 'PGRST116') {
        // No draw_match found for this event match schedule - not part of a draw
        return null;
      }
      console.error('Error finding draw_match:', drawMatchError);
      throw new Error('Failed to find draw match');
    }

    return drawMatch;
  },

  /**
   * The draws a match sits in, by either link: `draw_match.match_id`, or the match's schedule row
   * through `event_match_schedule_id`. The second is the usual one (`match_id` is set on few rows)
   * and the first can outlive the schedule row, so reading one link would miss a hidden draw.
   * Throws on a failed read, so a caller can tell "no draw" from "could not read".
   */
  async getDrawLinksForMatch(
    client: SupabaseClient<Database>,
    matchId: string,
    scheduleId: string | null,
  ): Promise<{ draw: { event_id: string; hidden_from_public: boolean } | null }[]> {
    const query = client.from('draw_match').select('draw:draw_id ( event_id, hidden_from_public )');
    const { data, error } = await (scheduleId
      ? query.or(`match_id.eq.${matchId},event_match_schedule_id.eq.${scheduleId}`)
      : query.eq('match_id', matchId));
    if (error) throw error;
    return data ?? [];
  },

  /**
   * Update draw match by ID
   * @param id - The draw match ID to update
   * @param data - The data to update
   * @param isAdmin - Whether to use admin client
   */
  async update(
    id: string,
    data: Database['public']['Tables']['draw_match']['Update'],
    isAdmin: boolean = false,
  ): Promise<void> {
    const client = isAdmin ? getSupabaseAdminClient() : createClient();
    if (!client) throw new Error('Supabase client not configured');

    const { error: updateError } = await client
      .from('draw_match')
      .update({
        ...data,
        updated_at: new Date().toISOString(),
      })
      .eq('id', id);

    if (updateError) {
      console.error('Error updating draw_match:', updateError);
      throw new Error('Failed to update draw match');
    }
  },

  /**
   * Process player advancement to next matches based on progression rules
   * @param drawId - The draw ID
   * @param drawMatchId - The completed draw match ID
   * @param winnerId - The winner player ID
   * @param loserId - The loser player ID
   * @param depth - Current recursion depth (internal use)
   * @param maxDepth - Maximum allowed recursion depth
   * @returns warnings for the admin — non-empty only when a completed qualifying pool's
   *   corrected standings could not be applied (`reseat_blocked`, nothing was written)
   */
  async processPlayerAdvancement(
    drawId: string,
    drawMatchId: string,
    winnerId: string,
    loserId: string,
    depth: number = 0,
    maxDepth: number = 3,
  ): Promise<AdvancementWarning[]> {
    // Prevent infinite recursion
    if (depth >= maxDepth) {
      console.error(
        `[ERROR] Maximum recursion depth (${maxDepth}) exceeded for match ${drawMatchId}`,
      );
      throw new Error(`Maximum recursion depth exceeded. Possible circular progression detected.`);
    }

    const client = getSupabaseAdminClient();
    if (!client) throw new Error('Supabase client not configured');

    // Get all progressions from this match
    const { data: progressions, error: progressionError } = await client
      .from('draw_match_progression')
      .select('*')
      .eq('from_match_id', drawMatchId);

    if (progressionError) {
      console.error('Error fetching match progressions:', progressionError);
      throw new Error('Failed to fetch match progressions');
    }

    if (!progressions || progressions.length === 0) {
      // Check if this is a round robin qualifying pool match
      return this.handleRoundRobinQualifyingAdvancement(
        client,
        drawMatchId,
        drawId,
        winnerId,
        loserId,
      );
    }

    // Process each progression
    for (const progression of progressions) {
      const playerId = progression.progression_type === 'winner' ? winnerId : loserId;

      // Nobody advances along this edge — a BYE has no loser, and a winner-less match
      // (dual_withdrawal / not_played) has neither. The seat it feeds is dead forever.
      if (!playerId) {
        // The MIRROR of the arrival-side check below: when the sibling feeder was
        // played first, its winner is ALREADY sitting in the target waiting for an
        // opponent who can never come. Nobody arrives to trigger that check, so the
        // dead edge has to resolve the target itself. Best-effort: a dead edge must
        // never abort the live ones (this read is new to a path that had none).
        const strandedTarget = await this.getDrawMatchById(progression.to_match_id).catch(
          (readError) => {
            console.error('Error reading winner-less progression target:', readError);
            return null;
          },
        );
        if (strandedTarget) {
          await this.maybeResolveWinnerlessOpponent(client, strandedTarget, depth, maxDepth);
        }
        continue;
      }

      // First, get the target draw_match to find its event_match_schedule_id
      const targetDrawMatch = await this.getDrawMatchById(progression.to_match_id);

      if (!targetDrawMatch) {
        console.error('Error fetching target draw match:', progression.to_match_id);
        throw new Error('Failed to fetch target draw match');
      }

      // Update the target draw_match with the advancing player
      await this.updateMatchWithPlayer(
        client,
        targetDrawMatch,
        playerId,
        progression.player_position as 'player1' | 'player2',
        'progression',
      );

      // Check if the target match now has a BYE and needs further advancement
      const updatedTargetMatch = await this.getDrawMatchById(progression.to_match_id);
      if (updatedTargetMatch && this.isMatchWithBye(updatedTargetMatch)) {
        // Recursively call processPlayerAdvancement for the BYE scenario
        // The player who was just placed "wins" the BYE and advances further
        await this.processPlayerAdvancement(
          drawId,
          progression.to_match_id,
          playerId, // The player who was just placed "wins" the BYE
          '', // No loser in BYE scenario
          depth + 1, // Increment depth
          maxDepth,
        );
      } else if (updatedTargetMatch) {
        // No BYE — but the other seat may be permanently empty because its feeder
        // resolved winner-less. If so, this match is already decided.
        await this.maybeResolveWinnerlessOpponent(client, updatedTargetMatch, depth, maxDepth);
      }
    }
    return [];
  },

  /** Read the complete rows needed by the pool correction feature planner. */
  async loadPoolCorrectionRows(drawId: string) {
    const client = getSupabaseAdminClient();
    if (!client) throw new Error('Supabase client not configured');
    const [stages, matches, draw] = await Promise.all([
      client.from('draw_stage').select('*').eq('draw_id', drawId),
      client.from('draw_match').select('*').eq('draw_id', drawId),
      client.from('draw').select('*').eq('id', drawId).single(),
    ]);
    if (stages.error || matches.error || draw.error || !draw.data)
      throw new Error('Could not read pool correction dependencies');
    const ids = matches.data.flatMap((m) =>
      m.event_match_schedule_id ? [m.event_match_schedule_id] : [],
    );
    const schedules = ids.length
      ? await client.from('event_match_schedule').select('*').in('id', ids)
      : { data: [], error: null };
    if (schedules.error) throw schedules.error;
    return {
      stages: stages.data,
      matches: matches.data,
      schedules: schedules.data ?? [],
      draw: draw.data,
    };
  },

  /**
   * Throws an error with `status: 400` if this match's result cannot be safely
   * reverted. Walks the progression graph downstream: a target is unwindable
   * only when it is still pending/ready and carries no bye flag, or when it was
   * itself auto-resolved as a walkover cascade (`completed` + `outcome`
   * `'walkover'`) AND everything downstream of *it* is unwindable too.
   *
   * FAILS CLOSED: an unreadable row, a failed query or a cascade deeper than the
   * cap refuses the revert. This sits directly upstream of the hard
   * `match.delete()` in `revertMatchResult` — over-blocking is a support ticket,
   * under-blocking is silent bracket corruption.
   */
  async assertCanRevertMatchResult(drawMatchId: string, source?: DrawMatch): Promise<void> {
    const client = getSupabaseAdminClient();
    if (!client) throw new Error('Supabase client not configured');
    const block = (msg: string) => Object.assign(new Error(msg), { status: 400 });

    const walk = async (fromId: string, depth: number): Promise<void> => {
      if (depth > REVERT_CASCADE_MAX_DEPTH) {
        throw block('Revert cascade too deep to verify — unwind downstream matches manually.');
      }
      const { data: progressions, error } = await client
        .from('draw_match_progression')
        .select('to_match_id')
        .eq('from_match_id', fromId);
      if (error) throw block('Could not verify downstream matches — refusing to revert.');

      for (const prog of progressions ?? []) {
        const { data: target, error: targetError } = await client
          .from('draw_match')
          .select(
            'status, outcome, player1_is_bye, player2_is_bye, player1_id, player2_id, loser_id, event_match_schedule:event_match_schedule_id(match:match_id(status))',
          )
          .eq('id', prog.to_match_id)
          .single();
        // Unknown downstream state is not "nothing to unwind" — refuse.
        if (targetError || !target) {
          throw block('Could not verify a downstream match — refusing to revert.');
        }
        // Starting the scorer links a match without changing draw_match.status.
        // A ready draw seat can therefore already be in play (or finalized while
        // its draw stamp is still pending); protect that canonical match too.
        const scoredStatus = target.event_match_schedule?.match?.status;
        if (scoredStatus === 'in_progress' || scoredStatus === 'completed') {
          throw block(
            scoredStatus === 'completed'
              ? 'The next match in the draw has already completed. Unwind it first.'
              : 'The next match in the draw has already started. Unwind it first.',
          );
        }
        // A bye on the target means the advanced player cascaded past it into
        // later rounds; the non-recursive revert cannot unwind that safely.
        if (target.player1_is_bye === true || target.player2_is_bye === true) {
          throw block(
            'This match feeds a bye that advanced the player into later rounds, which cannot be automatically reverted.',
          );
        }
        if (target.status === 'in_progress') {
          throw block('The next match in the draw has already started. Unwind it first.');
        }
        if (target.status === 'completed') {
          // An auto-cascade stamp (no loser, one seat) resolved itself because
          // nobody could ever arrive, so it unwinds with the revert — provided ITS
          // own downstream does too. Every other completion, an admin-entered
          // walkover included, is a real result somebody recorded on purpose.
          if (isCascadeWalkoverArtifact(target)) {
            await walk(prog.to_match_id, depth + 1);
            continue;
          }
          throw block('The next match in the draw has already completed. Unwind it first.');
        }
        // pending / ready: nothing has happened past here — stop walking.
      }
    };

    if (source?.stage_id) {
      const rows = await this.loadPoolCorrectionRows(source.draw_id);
      const snapshot = poolCorrectionSnapshot(
        source,
        rows.stages,
        rows.matches,
        rows.schedules,
        rows.draw,
      );
      if (snapshot) planPoolAdvancementUndo(snapshot);
    }
    await walk(drawMatchId, 0);
  },

  /**
   * Revert a completed or walkover draw match back to an un-scored state:
   * undo progressions (clear advanced players from downstream matches), delete
   * the match record (cascades match_game, match_game_point, rating), and reset
   * event_match_schedule and draw_match.
   *
   * Callers MUST have already passed `assertCanRevertMatchResult`.
   */
  async revertMatchResult(scheduleId: string, drawMatchId: string): Promise<void> {
    const client = getSupabaseAdminClient();
    if (!client) throw new Error('Supabase client not configured');

    const { data: drawMatch, error: drawMatchError } = await client
      .from('draw_match')
      .select('*')
      .eq('id', drawMatchId)
      .single();
    if (drawMatchError || !drawMatch) throw new Error('Failed to load draw match for revert');

    const { data: schedule, error: scheduleError } = await client
      .from('event_match_schedule')
      .select('match_id')
      .eq('id', scheduleId)
      .single();
    if (scheduleError || !schedule) throw new Error('Failed to load schedule for revert');

    // Inferred RR dependencies have no explicit progression edges. Validate the
    // entire snapshot before clearing any seat or deleting the source history.
    if (drawMatch.stage_id) {
      const rows = await this.loadPoolCorrectionRows(drawMatch.draw_id);
      const snapshot = poolCorrectionSnapshot(
        drawMatch,
        rows.stages,
        rows.matches,
        rows.schedules,
        rows.draw,
      );
      const plan = snapshot ? planPoolAdvancementUndo(snapshot) : [];
      for (const seat of plan) {
        if (seat.schedule) {
          const { data, error } = await client
            .from('event_match_schedule')
            .update({
              player_1_id: seat.player1Id,
              player_2_id: seat.player2Id,
            })
            .eq('id', seat.schedule.id)
            .eq('updated_at', seat.schedule.updated_at)
            .is('match_id', null)
            .select('id');
          if (error || data?.length !== 1)
            throw new Error(
              'Pool advancement schedule changed during undo; source result was retained.',
            );
        }
        const { data, error } = await client
          .from('draw_match')
          .update({
            player1_id: seat.player1Id,
            player2_id: seat.player2Id,
            status: seat.status,
          })
          .eq('id', seat.match.id)
          .eq('updated_at', seat.match.updated_at)
          .is('match_id', null)
          .select('id');
        if (error || data?.length !== 1)
          throw new Error(
            'Pool advancement changed during undo; reconcile its seats before retrying.',
          );
      }
    }

    // STEP 1: Undo progressions — clear advanced players from downstream matches.
    // A target that is `completed` with `outcome = 'walkover'` is a cascade
    // artefact: it was auto-stamped precisely because nobody could ever arrive in
    // the seat this match feeds. Merely unseating it would leave it half-unwound —
    // still carrying a winner, an outcome and a match_id — so it is unwound in
    // full: its OWN downstream seats first, then its result, its schedule row and
    // its match row. `assertCanRevertMatchResult` walks this exact shape under the
    // same depth cap and refuses anything it cannot fully unwind, so every state
    // this recursion touches has already been verified by the guard (callers MUST
    // have passed it). Pending/ready targets keep the plain seat-nulling.
    const unwindDownstream = async (fromId: string, depth: number): Promise<void> => {
      if (depth > REVERT_CASCADE_MAX_DEPTH) {
        // Unreachable through the guard, which blocks deeper cascades outright.
        throw new Error('Revert cascade too deep to unwind safely');
      }

      const { data: progressions, error: progError } = await client
        .from('draw_match_progression')
        .select('*')
        .eq('from_match_id', fromId);
      if (progError) throw progError;

      for (const prog of progressions ?? []) {
        const { data: targetMatch, error: targetError } = await client
          .from('draw_match')
          .select('*')
          .eq('id', prog.to_match_id)
          .single();
        // Fail loud where the guard fails closed: skipping an unreadable subtree
        // and then deleting the root's match row (STEP 2) is exactly the silent
        // corruption the guard exists to prevent.
        if (targetError || !targetMatch) {
          throw new Error('Failed to load a downstream match for revert');
        }

        const isCascadeWalkover = isCascadeWalkoverArtifact(targetMatch);
        // The guard verified a snapshot; this is a second read. A target that is
        // completed but not a cascade artefact changed under us (or is an
        // admin-entered walkover the guard would have blocked) — seat-nulling it
        // would half-reset a real result, so refuse instead.
        if (!isCascadeWalkover && targetMatch.status === 'completed') {
          throw new Error(
            'A downstream match completed since this revert was authorised — unwind it first',
          );
        }
        // Deepest seats first: clear what this target itself fed before clearing it.
        if (isCascadeWalkover) await unwindDownstream(targetMatch.id, depth + 1);

        const updateData: Database['public']['Tables']['draw_match']['Update'] & {
          status: 'pending' | 'ready';
        } = {
          status: 'pending',
        };
        let willHavePlayer1: boolean;
        let willHavePlayer2: boolean;
        if (prog.player_position === 'player1') {
          updateData.player1_id = null;
          willHavePlayer1 = false;
          willHavePlayer2 = !!targetMatch.player2_id;
        } else if (prog.player_position === 'player2') {
          updateData.player2_id = null;
          willHavePlayer1 = !!targetMatch.player1_id;
          willHavePlayer2 = false;
        } else {
          willHavePlayer1 = !!targetMatch.player1_id;
          willHavePlayer2 = !!targetMatch.player2_id;
        }
        updateData.status = willHavePlayer1 && willHavePlayer2 ? 'ready' : 'pending';
        if (isCascadeWalkover) {
          // outcome MUST be cleared or the mirror trigger (20260818070000)
          // resurrects is_walkover from the stale value.
          updateData.winner_id = null;
          updateData.loser_id = null;
          updateData.score = null;
          updateData.outcome = null;
          updateData.is_walkover = false;
          updateData.match_id = null;
        }

        await client.from('draw_match').update(updateData).eq('id', targetMatch.id);

        if (targetMatch.event_match_schedule_id) {
          const scheduleUpdate: Database['public']['Tables']['event_match_schedule']['Update'] = {};
          if (prog.player_position === 'player1') scheduleUpdate.player_1_id = null;
          else if (prog.player_position === 'player2') scheduleUpdate.player_2_id = null;
          if (isCascadeWalkover) {
            scheduleUpdate.match_id = null;
            scheduleUpdate.status = 'scheduled';
            scheduleUpdate.is_walkover = false;
            scheduleUpdate.outcome = null;
            scheduleUpdate.updated_at = new Date().toISOString();
          }
          await client
            .from('event_match_schedule')
            .update(scheduleUpdate)
            .eq('id', targetMatch.event_match_schedule_id);
        }

        // Null-safe: an auto-stamped cascade walkover carries no match row, but a
        // converted one does, and it cascades match_game / match_game_point / rating.
        if (isCascadeWalkover && targetMatch.match_id) {
          await client.from('match').delete().eq('id', targetMatch.match_id);
        }
      }
    };

    await unwindDownstream(drawMatchId, 0);

    // STEP 2: Delete the match record (cascades match_game, match_game_point, rating).
    if (schedule?.match_id) {
      await client.from('match').delete().eq('id', schedule.match_id);
    }

    // STEP 3: Reset event_match_schedule to un-scored.
    // outcome MUST be cleared here or the mirror trigger (20260818070000) resurrects
    // is_walkover from the stale value.
    await client
      .from('event_match_schedule')
      .update({
        match_id: null,
        status: 'scheduled',
        is_walkover: false,
        outcome: null,
        updated_at: new Date().toISOString(),
      })
      .eq('id', scheduleId);

    // STEP 4: Reset draw_match to un-scored.
    // outcome MUST be cleared here or the mirror trigger (20260818070000) resurrects
    // is_walkover from the stale value.
    const hasBothPlayers = !!(drawMatch.player1_id && drawMatch.player2_id);
    await this.update(
      drawMatchId,
      {
        status: hasBothPlayers ? 'ready' : 'pending',
        is_walkover: false,
        outcome: null,
        winner_id: null,
        loser_id: null,
        score: null,
        match_id: null,
      },
      true,
    );
  },

  /**
   * The real size of every pool (index 0 = pool 1). Prefers the sizes stored on a tier
   * stage at generation (`settings.poolSizes`); a draw generated before they existed (a
   * legacy `qualifying_plate`) has them counted from its pool stages — distinct players
   * across each `qualifying_pool_<n>` stage's matches. Returns null when neither source
   * has anything (the caller then keeps the saved options as they are).
   */
  async resolvePoolSizes(stages: DrawStage[], numPools: number): Promise<number[] | null> {
    for (const stage of stages) {
      const stored = (stage.settings as Partial<TierStageSettings> | null)?.poolSizes;
      if (Array.isArray(stored) && stored.length === numPools) return stored;
    }
    const poolStages = stages.filter((s) => /^qualifying_pool_\d+$/.test(s.stage_id));
    if (poolStages.length === 0) return null;
    const sizes = new Array<number>(numPools).fill(0);
    for (const stage of poolStages) {
      const poolNumber = this.extractPoolNumberFromStageId(stage.stage_id);
      if (poolNumber < 1 || poolNumber > numPools) continue;
      const players = new Set<string>();
      for (const m of await this.getDrawMatchesByStageId(stage.id)) {
        if (m.player1_id && m.player1_is_bye !== true) players.add(m.player1_id);
        if (m.player2_id && m.player2_is_bye !== true) players.add(m.player2_id);
      }
      sizes[poolNumber - 1] = players.size;
    }
    return sizes.some((n) => n > 0) ? sizes : null;
  },

  /**
   * Plan (without writing) this pool's seats in every advancement tier (tier ≥ 2).
   * Reads each tier stage's stored seat plan; a legacy `qualifying_plate` stage without
   * one gets the plan computed from the same pure functions. Tiers are resolved from the
   * REAL pool sizes (stored, or counted from the pool stages — see `resolvePoolSizes`),
   * so a legacy save carrying a preset's `playersPerPool` (Ultra Royale: 4, with 4 pools
   * of 3) resolves the plate it was actually generated with. A rank the pool cannot fill
   * becomes a dead seat. Per-tier containment: a tier whose plan cannot be built is
   * logged and left out, never stopping the tiers after it.
   */
  async planAdvancementTierSeats(
    drawId: string,
    poolNumber: number,
    standings: Array<{ playerId: string }>,
    savedOptions: DrawOptions,
  ): Promise<PlannedSeat[]> {
    const pools = savedOptions.qualifyingPools;
    if (resolveAdvancementTiers(savedOptions).every((t) => t.index < 2) || !pools) return [];
    const numPools = pools.numPools;
    const stages = await this.getDrawStages(drawId);
    const sizes = await this.resolvePoolSizes(stages, numPools);
    const options =
      sizes && Math.max(...sizes) > 0
        ? { ...savedOptions, qualifyingPools: { ...pools, playersPerPool: Math.max(...sizes) } }
        : savedOptions;
    const tiers = resolveAdvancementTiers(options).filter((t) => t.index >= 2);

    const seats: PlannedSeat[] = [];
    for (const tier of tiers) {
      const ctx = `draw ${drawId}, pool ${poolNumber}, stage ${tier.stageId}`;
      try {
        const stage = stages.find((s) => s.stage_id === tier.stageId);
        if (!stage) {
          console.warn(`advancement_tier: stage not found (${ctx})`);
          continue;
        }
        const rounds = await this.getDrawRoundsByStageId(stage.id);
        const firstRound = rounds.find((r) => r.round_number === 1) ?? rounds[0];
        if (!firstRound) {
          console.warn(`advancement_tier: stage has no first round (${ctx})`);
          continue;
        }
        const firstRoundMatches = (await this.getDrawMatchesByRoundId(firstRound.id)).sort(
          (a, b) => (a.match_number ?? 0) - (b.match_number ?? 0),
        );

        const stored = (stage.settings as Partial<TierStageSettings> | null)?.seatPlan;
        const tierSeats: PlannedSeat[] = [];
        let plan: SeatPlanEntry[];
        if (stored && stored.length > 0) {
          plan = stored;
        } else {
          // Legacy stage generated before seat plans existed. Its bracket was sized from the
          // ACTUAL remaining players; with the real pool sizes the tier plan matches it. A
          // bracket still smaller than the plan is a shape nothing maps onto, so it is
          // skipped rather than seated wrongly or partially.
          const actualSize = firstRoundMatches.length * 2;
          const expectedSize = tierDrawSize(tier, numPools);
          if (actualSize < expectedSize) {
            console.warn(
              `advancement_tier: legacy plate stage ${stage.stage_id} on draw ${drawId} is ${actualSize}-draw but the tier plan needs ${expectedSize}; unsupported legacy shape, regenerate the draw to adopt tiers (pool ${poolNumber})`,
            );
            continue;
          }
          plan = buildSeatPlan(tier, numPools, actualSize, 0, sizes ?? undefined);
          // Generated before tier stages got BYEs: a seat in no pool's plan (including a
          // short pool's missing rank) is never filled, so it is marked dead or whoever sits
          // opposite waits forever. An occupied seat (hand-seated) or one already flagged
          // is left alone.
          for (const position of unplannedSeatPositions(plan, actualSize)) {
            const match = firstRoundMatches[Math.floor((position - 1) / 2)];
            const seat = (position - 1) % 2 === 0 ? 'player1' : 'player2';
            if (!match || match[`${seat}_id`] || match[`${seat}_is_bye`] === true) continue;
            tierSeats.push({ stageId: tier.stageId, match, position: seat, playerId: null });
          }
        }

        for (let rank = tier.rankFrom; rank <= tier.rankTo; rank++) {
          const standing = standings[rank];
          const seat = plan.find((e) => e.pool === poolNumber && e.rank === rank);
          if (!seat) {
            // A short pool's missing rank has no seat by design (it was generated as a BYE)
            if (standing) console.warn(`advancement_tier: no seat for rank ${rank} (${ctx})`);
            continue;
          }
          const target = firstRoundMatches[seat.matchIndex];
          if (!target) {
            console.warn(
              `advancement_tier: no round-1 match at index ${seat.matchIndex} for rank ${rank} (${ctx})`,
            );
            continue;
          }
          tierSeats.push({
            stageId: tier.stageId,
            match: target,
            position: seat.position,
            playerId: standing?.playerId ?? null,
          });
        }
        seats.push(...tierSeats);
      } catch (error) {
        console.error(`advancement_tier: failed to seat tier (${ctx})`, error);
      }
    }
    return seats;
  },

  /**
   * Write planned tier seats: a player is (re)seated idempotently and cascaded past a
   * BYE opposite; a dead seat is marked (and propagated). Per-stage containment.
   */
  async applyAdvancementTierSeats(
    client: ReturnType<typeof createClient>,
    drawId: string,
    poolNumber: number,
    seats: PlannedSeat[],
  ): Promise<void> {
    for (const seat of seats) {
      try {
        if (seat.playerId === null) {
          await this.markSeatDead(
            client,
            drawId,
            seat.match,
            seat.position,
            poolNumber,
            seat.stageId,
          );
          continue;
        }
        const wrote = await this.reseatPlayer(
          client,
          seat.match,
          seat.playerId,
          seat.position,
          'advancement_tier',
        );
        if (!wrote) continue;
        const updated = await this.getDrawMatchById(seat.match.id);
        if (updated && this.isMatchWithBye(updated)) {
          // The seated player "wins" the BYE and cascades forward (same as the main draw)
          await this.processPlayerAdvancement(
            drawId,
            seat.match.id,
            seat.playerId,
            '',
            1,
            BYE_CASCADE_MAX_DEPTH,
          );
        }
      } catch (error) {
        console.error(
          `advancement_tier: failed to seat tier (draw ${drawId}, pool ${poolNumber}, stage ${seat.stageId})`,
          error,
        );
      }
    }
  },

  /**
   * Seat this pool's finishing positions into every advancement tier (tier ≥ 2):
   * `planAdvancementTierSeats` then `applyAdvancementTierSeats`. Idempotent via
   * `reseatPlayer`. The pool-completion path plans and applies separately so it can
   * refuse the whole re-seat (main + tiers) when any one seat is blocked.
   */
  async seatAdvancementTiers(
    client: ReturnType<typeof createClient>,
    drawId: string,
    poolNumber: number,
    standings: Array<{ playerId: string }>,
    options: DrawOptions,
  ): Promise<void> {
    const seats = await this.planAdvancementTierSeats(drawId, poolNumber, standings, options);
    await this.applyAdvancementTierSeats(client, drawId, poolNumber, seats);
  },

  /**
   * A pool has no player for this seat (a short pool, a later withdrawal). Mark it a BYE,
   * then make sure the dead seat strands nobody:
   * - a BYE always loses, so every LOSER edge out of this match carries a dead seat on
   *   (monrad sub-brackets), exactly as the generator's cascadeByes places BYE losers;
   * - if the opponent is seated, they win the BYE and cascade forward now;
   * - if the opposite seat is itself a BYE (dead v dead / dead v BYE), nobody can ever
   *   come out of this match, so the WINNER edge is dead too: the round-2 seat it feeds
   *   is marked dead in turn, which cascades whoever already waits there.
   * An empty, un-flagged opposite seat can still be filled — its own seating will
   * cascade past this BYE — so nothing more happens then.
   */
  async markSeatDead(
    client: ReturnType<typeof createClient>,
    drawId: string,
    snapshot: DrawMatch,
    position: 'player1' | 'player2',
    poolNumber: number,
    stageId: string,
    depth: number = 0,
  ): Promise<void> {
    // The caller's row may predate seats this same run wrote (a short single pool's #3 seated
    // opposite its missing #4): read the match afresh so the flag and opponent checks see them.
    // Best-effort like the other reads on this path — `getDrawMatchById` throws on any error,
    // and a seat left un-flagged (or a parent's remaining edges abandoned) is the stranded
    // player this exists to prevent, so a failed read falls back to the caller's row.
    const dbMatch =
      (await this.getDrawMatchById(snapshot.id).catch((readError) => {
        console.error(
          `advancement_tier: could not re-read match ${snapshot.id} before marking a seat dead`,
          readError,
        );
        return null;
      })) ?? snapshot;
    const byeField = position === 'player1' ? 'player1_is_bye' : 'player2_is_bye';
    if (dbMatch[byeField] === true) return;
    const ctx = `draw ${drawId}, pool ${poolNumber}, stage ${stageId}, match ${dbMatch.id}`;
    const { error } = await client
      .from('draw_match')
      .update({ [byeField]: true, updated_at: new Date().toISOString() })
      .eq('id', dbMatch.id);
    if (error) {
      console.error(`advancement_tier: failed to mark seat as BYE (${ctx})`, error);
      return;
    }
    const opponentPosition = position === 'player1' ? 'player2' : 'player1';
    const opponent = dbMatch[`${opponentPosition}_id`];
    const opponentDead = !opponent && dbMatch[`${opponentPosition}_is_bye`] === true;

    const { data: edges, error: edgeError } = await client
      .from('draw_match_progression')
      .select('to_match_id, player_position, progression_type')
      .eq('from_match_id', dbMatch.id);
    if (edgeError) {
      console.error(`advancement_tier: could not read where a dead seat leads (${ctx})`, edgeError);
    }
    for (const edge of Array.isArray(edges) ? edges : []) {
      if (edge.progression_type !== 'loser' && !opponentDead) continue;
      if (depth >= DEAD_SEAT_MAX_DEPTH) {
        console.error(`advancement_tier: dead-seat propagation too deep, stopped (${ctx})`);
        break;
      }
      const target = await this.getDrawMatchById(edge.to_match_id);
      const targetPosition = edge.player_position as 'player1' | 'player2';
      if (!target || target[`${targetPosition}_id`]) continue;
      await this.markSeatDead(
        client,
        drawId,
        target,
        targetPosition,
        poolNumber,
        stageId,
        depth + 1,
      );
    }

    if (opponent) {
      await this.processPlayerAdvancement(
        drawId,
        dbMatch.id,
        opponent,
        '',
        1,
        BYE_CASCADE_MAX_DEPTH,
      );
    }
  },

  /**
   * Seats a correction must never overwrite (I5): occupants who are lucky losers — a
   * seeding row still flagged `is_lucky_loser_slot` (the withdrawn player holding the seat
   * until one is assigned) or a player assigned as a lucky loser
   * (`replaced_by_registration_id`) — and standings players who have withdrawn. Same
   * columns the lucky-loser tool reads (lucky-loser/libs/withdrawal.ts, luckyLoserModel).
   * Fails open: a lookup error is logged and treated as "nothing protected", which is the
   * behaviour before the guard existed.
   */
  async getSeatGuards(
    client: ReturnType<typeof createClient>,
    drawId: string,
  ): Promise<SeatGuards> {
    const luckyLoserOccupants = new Set<string>();
    const withdrawn = new Set<string>();
    try {
      const { data: slots, error: slotError } = await client
        .from('draw_seeding')
        .select('player_id')
        .eq('draw_id', drawId)
        .eq('is_lucky_loser_slot', true);
      if (slotError) throw slotError;
      for (const row of slots ?? []) if (row.player_id) luckyLoserOccupants.add(row.player_id);

      const { data: draw, error: drawError } = await client
        .from('draw')
        .select('event_group_id')
        .eq('id', drawId)
        .single();
      if (drawError) throw drawError;
      if (draw?.event_group_id) {
        const { data: regs, error: regError } = await client
          .from('event_group_registration')
          .select('player_id, ll:replaced_by_registration_id(player_id)')
          .eq('event_group_id', draw.event_group_id)
          .eq('status', 'withdrawn');
        if (regError) throw regError;
        type Row = { player_id: string | null; ll: { player_id: string | null } | null };
        for (const row of (regs ?? []) as unknown as Row[]) {
          if (row.player_id) withdrawn.add(row.player_id);
          if (row.ll?.player_id) luckyLoserOccupants.add(row.ll.player_id);
        }
      }
    } catch (error) {
      console.error(
        `advancement: could not read lucky-loser/withdrawal state for draw ${drawId}`,
        error,
      );
      return { luckyLoserOccupants: new Set(), withdrawn: new Set() };
    }
    return { luckyLoserOccupants, withdrawn };
  },

  /**
   * Handle round robin qualifying pool advancement based on standings.
   *
   * All-or-nothing: every seat this pool's standings imply — main draw AND every tier —
   * is planned first; each seat that changes hands on a DECIDED match is pre-checked with
   * `reseatBlockedByDownstream`. If any is blocked, NOTHING is written and a
   * `reseat_blocked` warning is returned for the caller to show the admin (a half-applied
   * correction would leave one player in two brackets). Seats held by a lucky loser, or
   * that would go to a withdrawn player, are skipped (I5).
   *
   * @param client - Supabase client
   * @param drawMatchId - The completed draw match ID
   * @param drawId - The draw ID (passed from API to avoid additional queries)
   * @param winnerId - The winner player ID
   * @param loserId - The loser player ID
   * @returns warnings for the admin (empty when everything was seated)
   */
  async handleRoundRobinQualifyingAdvancement(
    client: ReturnType<typeof createClient>,
    drawMatchId: string,
    drawId: string,
    _winnerId: string,
    _loserId: string,
  ): Promise<AdvancementWarning[]> {
    // First, find which stage this match belongs to
    const match = await this.getDrawMatchById(drawMatchId);
    if (!match?.stage_id) {
      return [];
    }

    const stage = await this.getDrawStageById(match.stage_id);
    if (!stage) {
      return [];
    }

    // Only process qualifying round-robin pools
    if (!this.validateRoundRobinStage(stage)) {
      return [];
    }

    // Check if all matches in this round-robin stage are completed
    const allMatches = await this.getDrawMatchesByStageId(stage.id);

    if (!this.validateAllMatchesCompleted(allMatches)) {
      return [];
    }

    // Calculate standings for this stage
    const standings = await this.calculateRoundRobinStandings(stage.id);
    if (!standings || standings.length === 0) {
      return [];
    }

    // Find the main draw stage to advance players to
    const mainDrawStage = await this.findMainDrawStage(drawId);
    if (!mainDrawStage) {
      return [];
    }

    // Ladder template advancement: detect via settings and delegate
    const templateId = (mainDrawStage.settings as Record<string, unknown> | null)
      ?.ladderTemplateId as string | undefined;
    if (templateId === POOL_PLAYOFFS_TEMPLATE_ID) {
      // Two-pool placement: gate on ALL pools, read both pools' standings.
      await this.handlePoolPlayoffAdvancement(client, drawId, mainDrawStage);
      return [];
    }
    if (templateId) {
      await this.handleTemplateAdvancement(client, drawId, mainDrawStage, standings);
      return [];
    }

    // Standard qualifying pool advancement
    // Get all matches from the first round of the main draw
    const allRoundMatches = await this.getMainDrawFirstRoundMatches(mainDrawStage);

    // Determine the qualifying pool number from the stage
    const qualifyingPoolNumber = this.extractPoolNumberFromStageId(stage.stage_id);

    // Get draw configuration to determine qualifying pools and seeded players
    const { totalQualifyingPools, seededPlayers, advancementsPerPool } =
      await this.getQualifyingPoolConfiguration(client, drawId);

    // Get the advancing players for THIS pool
    const advancingPlayers = standings.slice(0, advancementsPerPool);

    // ── Plan: main-draw seats (same targets the single/multi advancer paths write) ──
    const mainSeats: Array<PlannedSeat & { playerId: string; placement?: AdvancementPlacement }> =
      [];
    if (advancementsPerPool === 1) {
      const player = advancingPlayers[0];
      const target = this.getQualifyingPoolTargetPosition(
        qualifyingPoolNumber,
        allRoundMatches,
        totalQualifyingPools,
        seededPlayers,
        advancementsPerPool,
      );
      const targetMatch = target && allRoundMatches.find((m) => m.id === target.matchId);
      if (player && target && targetMatch) {
        mainSeats.push({
          stageId: 'main',
          match: targetMatch,
          position: target.playerPosition,
          playerId: player.playerId,
        });
      } else if (player) {
        // advancePlayerToMainDraw reports (and throws on) an unresolvable seat as before
        mainSeats.push({
          stageId: 'main',
          match: allRoundMatches[0],
          position: 'player1',
          playerId: player.playerId,
          unresolved: true,
        });
      }
    } else {
      // For multiple advancements per pool, use the pure function approach
      const separatedPlacements = applySeparationLogic(
        calculateQualifyingPoolAdvancements({
          totalPools: totalQualifyingPools,
          advancementsPerPool,
          seededPlayers,
          drawSize: allRoundMatches.length * 2,
        }),
        allRoundMatches.length * 2,
      );
      for (let rank = 0; rank < advancingPlayers.length; rank++) {
        const placement = separatedPlacements.find(
          (p) => p.poolNumber === qualifyingPoolNumber && p.playerRank === rank,
        );
        if (!placement) {
          console.error(`No placement found for pool ${qualifyingPoolNumber}, rank ${rank}`);
          continue;
        }
        mainSeats.push({
          stageId: 'main',
          match: allRoundMatches[placement.matchIndex],
          position: placement.playerPosition,
          playerId: advancingPlayers[rank].playerId,
          placement,
        });
      }
    }

    // ── Plan: tier seats (Bowl/Plate …), from the same standings ──
    // A tier planning failure is logged, never thrown — it must not fail the pool-match
    // save that triggered it (a later save re-runs idempotently).
    let tierSeats: PlannedSeat[] = [];
    try {
      const drawSettings = await this.getDrawSettings(drawId);
      const options = (drawSettings?.options as DrawOptions | undefined) ?? {};
      tierSeats = await this.planAdvancementTierSeats(
        drawId,
        qualifyingPoolNumber,
        standings,
        options,
      );
    } catch (error) {
      console.error(`advancement_tier: failed to seat tiers for draw ${drawId}`, error);
    }

    // ── Guard: lucky-loser seats and withdrawn players are never re-seated (I5) ──
    const displaces = (seat: PlannedSeat) => {
      if (seat.playerId === null || !seat.match || seat.unresolved) return false;
      const occupant = seat.match[`${seat.position}_id`];
      return !!occupant && occupant !== seat.playerId;
    };
    const planned = [...mainSeats, ...tierSeats];
    let skip = new Set<PlannedSeat>();
    const skipReason = new Map<PlannedSeat, string>();
    if (planned.some(displaces)) {
      const guards = await this.getSeatGuards(client, drawId);
      skip = new Set(
        planned.filter((seat) => {
          if (!displaces(seat)) return false;
          const occupant = seat.match[`${seat.position}_id`] as string;
          const where = `draw ${drawId}, pool ${qualifyingPoolNumber}, stage ${seat.stageId}, match ${seat.match.id} ${seat.position}`;
          if (guards.luckyLoserOccupants.has(occupant)) {
            console.info(
              `advancement: not re-seating ${where} — ${occupant} holds a lucky-loser seat`,
            );
            skipReason.set(seat, LUCKY_LOSER_OCCUPANT_REASON);
            return true;
          }
          if (guards.withdrawn.has(seat.playerId as string)) {
            console.info(
              `advancement: not re-seating ${where} — ${seat.playerId} has withdrawn (${occupant} keeps the seat)`,
            );
            skipReason.set(seat, WITHDRAWN_OCCUPANT_REASON);
            return true;
          }
          return false;
        }),
      );
    }
    const toWrite = planned.filter((seat) => !skip.has(seat));

    // ── Pair check (NB-2): a skipped seat keeps its occupant in place. If this batch also
    // moves that occupant into another seat — whether that other change is written or is
    // itself skipped (a 'keep'-mode withdrawal is both a withdrawn player and a flagged
    // lucky-loser slot, so its swap skips twice and used to do nothing, silently) — the
    // batch cannot be applied consistently. Refuse all of it (fail closed) and say why:
    // the admin resolves the withdrawal / lucky-loser seat first, then re-saves the result.
    const moves = planned.filter(
      (seat) =>
        seat.playerId !== null &&
        !seat.unresolved &&
        seat.match[`${seat.position}_id`] !== seat.playerId,
    );
    const pairConflicts = [...skipReason.keys()].filter((skipped) => {
      const occupant = skipped.match[`${skipped.position}_id`];
      return moves.some((other) => other !== skipped && other.playerId === occupant);
    });
    if (pairConflicts.length > 0) {
      console.warn(
        `advancement: pool ${qualifyingPoolNumber} of draw ${drawId} not re-seated — ${pairConflicts
          .map(
            (seat) =>
              `${seat.match.id}: kept for ${seat.match[`${seat.position}_id`]} (${skipReason.get(seat)}), who this correction also moves elsewhere`,
          )
          .join(
            '; ',
          )} — resolve the withdrawal / lucky-loser seat first, then re-save the pool result`,
      );
      return [
        {
          code: 'reseat_blocked',
          drawId,
          poolNumber: qualifyingPoolNumber,
          matches: pairConflicts.map((seat) => ({
            matchId: seat.match.id,
            drawMatchId: seat.match.draw_match_id ?? null,
            stageId: seat.stageId,
            reason: skipReason.get(seat) as string,
          })),
        },
      ];
    }

    // ── Pre-check: a re-seat on a decided match must not strand a result that moved on ──
    const blocked: AdvancementWarning['matches'] = [];
    for (const seat of toWrite) {
      if (seat.playerId === null || seat.unresolved) continue;
      if (!this.reseatNeedsGuard(seat.match, seat.position, seat.playerId)) continue;
      const reason = await this.reseatBlockedByDownstream(client, seat.match);
      if (reason) {
        blocked.push({
          matchId: seat.match.id,
          drawMatchId: seat.match.draw_match_id ?? null,
          stageId: seat.stageId,
          reason,
        });
      }
    }
    if (blocked.length > 0) {
      console.warn(
        `advancement: pool ${qualifyingPoolNumber} of draw ${drawId} not re-seated — ${blocked
          .map((b) => `${b.matchId}: ${b.reason}`)
          .join('; ')} — revert the downstream result first, then re-save the pool result`,
      );
      return [
        { code: 'reseat_blocked', drawId, poolNumber: qualifyingPoolNumber, matches: blocked },
      ];
    }

    // ── Apply: main draw first, then the tiers ──
    for (const seat of mainSeats) {
      if (skip.has(seat)) continue;
      if (seat.placement) {
        await this.advancePlayerToMainDrawWithPlacement(
          client,
          drawId,
          { playerId: seat.playerId },
          seat.placement,
          allRoundMatches,
        );
      } else {
        await this.advancePlayerToMainDraw(
          client,
          drawId,
          { playerId: seat.playerId },
          qualifyingPoolNumber,
          allRoundMatches,
          totalQualifyingPools,
          seededPlayers,
          advancementsPerPool,
        );
      }
    }
    try {
      await this.applyAdvancementTierSeats(
        client,
        drawId,
        qualifyingPoolNumber,
        tierSeats.filter((seat) => !skip.has(seat)),
      );
    } catch (error) {
      console.error(`advancement_tier: failed to seat tiers for draw ${drawId}`, error);
    }
    return [];
  },

  /**
   * Handle advancement from RR standings into a ladder template stage.
   * Maps standings to template-defined slots and places players into the ladder matches.
   */
  async handleTemplateAdvancement(
    client: ReturnType<typeof createClient>,
    drawId: string,
    ladderStage: DrawStage,
    standings: Array<{
      playerId: string;
      matchesPlayed: number;
      matchesWon: number;
      matchesLost: number;
      gamesWon: number;
      gamesLost: number;
      pointsWon: number;
      pointsLost: number;
    }>,
  ): Promise<void> {
    const templateId = (ladderStage.settings as Record<string, unknown> | null)
      ?.ladderTemplateId as string;
    const template = getTemplate(templateId);
    if (!template) {
      console.error(`Ladder template not found: ${templateId}`);
      return;
    }

    // Sort by wins desc, then game point diff, to ensure correct ranking
    const sorted = [...standings].sort((a, b) => {
      if (b.matchesWon !== a.matchesWon) return b.matchesWon - a.matchesWon;
      return b.pointsWon - b.pointsLost - (a.pointsWon - a.pointsLost);
    });

    const playerStandings: PlayerStanding[] = sorted.map((s, i) => ({
      playerId: s.playerId,
      playerName: '',
      position: i + 1,
      wins: s.matchesWon,
      losses: s.matchesPlayed - s.matchesWon,
      points: 0,
      gamesWon: 0,
      gamesLost: 0,
      gamePointsFor: s.pointsWon,
      gamePointsAgainst: s.pointsLost,
      gamePointsPercentage:
        s.pointsWon + s.pointsLost > 0 ? s.pointsWon / (s.pointsWon + s.pointsLost) : 0,
    }));

    const assignments = mapStandingsToLadderSlots(playerStandings, template);

    // Get all ladder stage matches to find DB records by draw_match_id
    const ladderMatches = await this.getDrawMatchesByStageId(ladderStage.id);

    for (const assignment of assignments) {
      const dbMatch = ladderMatches.find((m) => m.draw_match_id === `main-${assignment.matchId}`);
      if (!dbMatch) {
        console.error(`Ladder match not found for template match: ${assignment.matchId}`);
        continue;
      }

      await this.updateMatchWithPlayer(
        client,
        dbMatch,
        assignment.playerId,
        assignment.position,
        'template_advancement',
      );
    }
  },

  /**
   * Seat `playerId` at `position` of `dbMatch`, idempotently.
   *
   * No-clobber: if the seat already holds this player nothing is written (the match
   * may already have been played). When the occupant actually changes (a corrected
   * pool result re-ordered standings) any stale result is cleared and the match
   * re-opened in the SAME write, and the mirrored schedule row is unlinked, so the
   * match is replayed cleanly rather than left inconsistent. Shared by pool play-offs
   * and advancement tiers.
   *
   * A DECIDED match is re-seated only when its result has not moved on: the fail-closed
   * `assertCanRevertMatchResult` must pass AND no progression target (next round, or a
   * loser feed into another stage) may still hold the old winner/loser. Otherwise the
   * clear would leave a later round holding a player who is no longer in this match
   * (the same player in two brackets) — so nothing is written, an actionable warning is
   * logged, and false is returned. Applies to every caller (main draw, tiers, pool
   * play-offs).
   *
   * @returns true when a seat write happened, false when it was a no-op or refused
   */
  async reseatPlayer(
    client: ReturnType<typeof createClient>,
    dbMatch: DrawMatch,
    playerId: string,
    position: 'player1' | 'player2',
    context: string,
  ): Promise<boolean> {
    const current = position === 'player1' ? dbMatch.player1_id : dbMatch.player2_id;
    if (current === playerId) return false;

    if (this.reseatNeedsGuard(dbMatch, position, playerId)) {
      const blocked = await this.reseatBlockedByDownstream(client, dbMatch);
      if (blocked) {
        console.warn(
          `${context}: not re-seating draw ${dbMatch.draw_id} match ${dbMatch.id} ${position} (${current ?? 'empty'} → ${playerId}): ${blocked} — revert the downstream result first, then re-save the pool result`,
        );
        return false;
      }
    }

    await this.updateMatchWithPlayer(client, dbMatch, playerId, position, context);

    if (dbMatch.winner_id || dbMatch.status === 'completed') {
      // `outcome` cleared + `is_walkover` explicitly false: the mirror trigger
      // (20260818070000) is inert when outcome lands NULL, so the flag must be
      // dropped by hand or a walkover placement keeps rendering as W/O.
      // `status` is re-opened HERE, in the same write that clears the result: this
      // re-seat is a deliberate re-open, but `updateMatchWithPlayer` deliberately does
      // NOT downgrade a decided match (that would break the cascade-artifact shape for
      // its other callers). One write, so the row is never observable as a completed
      // match with no winner.
      const { error: clearError } = await client
        .from('draw_match')
        .update({
          winner_id: null,
          loser_id: null,
          score: null,
          outcome: null,
          is_walkover: false,
          match_id: null,
          status: 'ready',
          updated_at: new Date().toISOString(),
        })
        .eq('id', dbMatch.id);
      if (clearError) {
        console.error(`${context}: failed to clear stale result`, clearError);
      } else if (dbMatch.event_match_schedule_id) {
        // The schedule row mirrors the result too — and `match_id` MUST be severed:
        // it is the "this schedule already has a match" claim token. Kept, it makes
        // the re-seated match unscoreable (from-schedule scoring short-circuits on
        // it, `linkScheduleToMatchIfUnlinked` claims with `.is('match_id', null)`)
        // and public schedule/standings displays join the stale result straight
        // through it. Severing orphans the old match record (games, ratings,
        // activity cards — follow-up 13 owns the delete + rating rebuild), so log
        // its id first to keep it recoverable. The trigger is inert on outcome
        // NULL, so the explicit is_walkover:false is load-bearing. Ordered AFTER
        // the draw_match clear on purpose: if that write failed, both rows stay
        // stale together instead of drifting apart.
        const { data: staleSchedule } = await client
          .from('event_match_schedule')
          .select('match_id')
          .eq('id', dbMatch.event_match_schedule_id)
          .single();
        if (staleSchedule?.match_id) {
          console.warn(
            `${context}: re-seat orphans match record ${staleSchedule.match_id} (schedule ${dbMatch.event_match_schedule_id}) — result data preserved but unlinked`,
          );
        }
        const { error: scheduleClearError } = await client
          .from('event_match_schedule')
          .update({
            match_id: null,
            status: 'scheduled',
            is_walkover: false,
            outcome: null,
            updated_at: new Date().toISOString(),
          })
          .eq('id', dbMatch.event_match_schedule_id);
        if (scheduleClearError) {
          console.error(`${context}: failed to clear stale schedule row`, scheduleClearError);
        }
      }
    }
    return true;
  },

  /**
   * Whether putting `playerId` at `position` replaces someone on a match that is already
   * DECIDED — played (`winner_id` / `completed`), or a BYE match whose occupant has
   * already cascaded past the BYE into the next round (I3: a BYE match is never stamped,
   * but its player has moved on just the same). Such a re-seat must pass
   * `reseatBlockedByDownstream` first. First seatings and undecided matches need no check.
   */
  reseatNeedsGuard(dbMatch: DrawMatch, position: 'player1' | 'player2', playerId: string): boolean {
    const current = position === 'player1' ? dbMatch.player1_id : dbMatch.player2_id;
    if (current === playerId) return false;
    return (
      !!dbMatch.winner_id ||
      dbMatch.status === 'completed' ||
      (this.isMatchWithBye(dbMatch) && !!current)
    );
  },

  /**
   * Why a decided match must not be re-seated, or null when it safely can be. Fails
   * closed: an unreadable progression or target row is a reason to refuse.
   *
   * A BYE match (never stamped: no winner/loser) goes through `byeCascadeBlocked`, which
   * follows its occupant's cascade through BYE-flagged rounds and refuses only on a match
   * that was really started or played — the re-seat's own BYE cascade overwrites every
   * hop. `assertCanRevertMatchResult` is NOT used there: it refuses any bye-flagged target
   * outright (right for the revert route, which cannot re-cascade), and C1's BYE-v-BYE
   * tier matches put a bye flag on almost every round-2 match (NB-1).
   */
  async reseatBlockedByDownstream(
    client: ReturnType<typeof createClient>,
    dbMatch: DrawMatch,
  ): Promise<string | null> {
    if (this.isMatchWithBye(dbMatch) && !dbMatch.winner_id && dbMatch.status !== 'completed') {
      return this.byeCascadeBlocked(client, dbMatch.id, 0);
    }
    try {
      await this.assertCanRevertMatchResult(dbMatch.id);
    } catch (error) {
      return error instanceof Error ? error.message : 'downstream matches could not be verified';
    }

    const decided = [dbMatch.winner_id, dbMatch.loser_id].filter((id): id is string => !!id);
    if (decided.length === 0) return null;

    const { data: progressions, error } = await client
      .from('draw_match_progression')
      .select('to_match_id')
      .eq('from_match_id', dbMatch.id);
    if (error) return 'could not read where this match progresses';

    for (const progression of progressions ?? []) {
      const { data: target, error: targetError } = await client
        .from('draw_match')
        .select('player1_id, player2_id')
        .eq('id', progression.to_match_id)
        .single();
      if (targetError || !target) {
        return `could not read downstream match ${progression.to_match_id}`;
      }
      const stale = decided.find((id) => id === target.player1_id || id === target.player2_id);
      if (stale) {
        return `player ${stale} from this result already sits in match ${progression.to_match_id}`;
      }
    }
    return null;
  },

  /**
   * Walk a BYE occupant's cascade downstream (WINNER edges only — a BYE match's loser edge
   * carries the BYE, which a re-seat never changes). Each hop:
   * - `in_progress` → refuse (someone is playing the old occupant);
   * - `completed` → refuse, whatever the result — a real one or the cascade's own walkover
   *   artifact alike: the re-seat's cascade cannot rewrite a completed hop
   *   (`updateMatchWithPlayer` keeps its status and winner, `maybeResolveWinnerlessOpponent`
   *   skips it), so the old occupant would stay its winner and in every round after it;
   * - BYE-flagged and undecided → the occupant cascaded past it: keep walking;
   * - otherwise (`pending`/`ready`) → stop: the cascade overwrites this seat.
   * Fails closed on an unreadable row or a chain longer than `BYE_CASCADE_MAX_HOPS`.
   */
  async byeCascadeBlocked(
    client: ReturnType<typeof createClient>,
    fromMatchId: string,
    depth: number,
  ): Promise<string | null> {
    // This call reads hop `depth + 1`; the re-seat's cascade can write BYE_CASCADE_MAX_HOPS.
    if (depth >= BYE_CASCADE_MAX_HOPS) return 'BYE cascade too deep to verify';
    const { data: progressions, error } = await client
      .from('draw_match_progression')
      .select('to_match_id, progression_type')
      .eq('from_match_id', fromMatchId);
    if (error) return 'could not read where this match progresses';

    for (const progression of progressions ?? []) {
      if (progression.progression_type === 'loser') continue;
      const { data: target, error: targetError } = await client
        .from('draw_match')
        .select('status, player1_is_bye, player2_is_bye')
        .eq('id', progression.to_match_id)
        .single();
      if (targetError || !target) {
        return `could not read downstream match ${progression.to_match_id}`;
      }
      if (target.status === 'in_progress') {
        return `match ${progression.to_match_id} further down this BYE cascade has already started`;
      }
      if (target.status === 'completed') {
        return `match ${progression.to_match_id} further down this BYE cascade has already completed`;
      }
      const byeFlagged = target.player1_is_bye === true || target.player2_is_bye === true;
      if (!byeFlagged) continue; // pending / ready: the re-seat's cascade overwrites this seat
      const reason = await this.byeCascadeBlocked(client, progression.to_match_id, depth + 1);
      if (reason) return reason;
    }
    return null;
  },

  /**
   * Two-pool placement advancement. Fires on each pool-match completion and seeds each
   * pool's side of the placement matches as soon as THAT pool is fully scored (partial
   * advancement): pool_1 -> player1/'A' slots, pool_2 -> player2/'B' slots. A match is
   * playable once both sides are filled; an orphan bye is finalised when its pool finishes.
   */
  async handlePoolPlayoffAdvancement(
    client: ReturnType<typeof createClient>,
    drawId: string,
    placementStage: DrawStage,
  ): Promise<void> {
    const allStages = await this.getDrawStages(drawId);
    const poolStages = allStages
      .filter((s) => s.type === 'round_robin' && s.context === 'qualifying')
      .sort((a, b) => a.stage_id.localeCompare(b.stage_id)); // _1 before _2

    if (poolStages.length < 2) return;

    const template = (placementStage.settings as Record<string, unknown> | null)?.ladderTemplate as
      | LadderTemplate
      | undefined;
    if (!template) {
      console.error('Pool play-offs: placement stage missing stored ladderTemplate');
      return;
    }

    // Partial advancement: build standings ONLY for pools that are fully scored
    // (pool_1 -> 'A', pool_2 -> 'B'). Each completed pool seeds its side of the
    // placement matches immediately; the missing side fills when its pool finishes.
    const poolKeys: ('A' | 'B')[] = ['A', 'B'];
    const standingsByPool = new Map<'A' | 'B', PlayerStanding[]>();
    for (let i = 0; i < poolStages.length && i < 2; i++) {
      const poolMatches = await this.getDrawMatchesByStageId(poolStages[i].id);
      if (!this.validateAllMatchesCompleted(poolMatches)) continue; // this pool not done yet
      const standings = await this.calculateRoundRobinStandings(poolStages[i].id);
      // calculateRoundRobinStandings returns rows pre-sorted by rank; project them
      // into PlayerStanding shape with an explicit 1-indexed position for the resolver.
      const playerStandings: PlayerStanding[] = standings.map((s, index) => ({
        playerId: s.playerId,
        playerName: '',
        position: index + 1,
        wins: s.matchesWon,
        losses: s.matchesLost,
        points: 0,
        gamesWon: s.gamesWon,
        gamesLost: s.gamesLost,
        gamePointsFor: s.pointsWon,
        gamePointsAgainst: s.pointsLost,
        gamePointsPercentage:
          s.pointsWon + s.pointsLost > 0 ? s.pointsWon / (s.pointsWon + s.pointsLost) : 0,
      }));
      standingsByPool.set(poolKeys[i], playerStandings);
    }

    if (standingsByPool.size === 0) return; // no pool fully scored yet

    const assignments = mapPoolStandingsToLadderSlots(standingsByPool, template);
    const placementMatches = await this.getDrawMatchesByStageId(placementStage.id);

    for (const assignment of assignments) {
      const dbMatch = placementMatches.find(
        (m) => m.draw_match_id === `main-${assignment.matchId}`,
      );
      if (!dbMatch) {
        console.error(`Pool play-offs: placement match not found for ${assignment.matchId}`);
        continue;
      }
      await this.reseatPlayer(
        client,
        dbMatch,
        assignment.playerId,
        assignment.position,
        'pool_playoff_advancement',
      );
    }

    // Finalise an orphan bye match (odd counts) only once the orphan's own pool is complete.
    for (const tm of template.matches) {
      const byeIsP1 = tm.player1.from === 'bye';
      const byeIsP2 = tm.player2.from === 'bye';
      if (!byeIsP1 && !byeIsP2) continue;
      const orphanSlot = byeIsP1 ? tm.player2 : tm.player1;
      if (!orphanSlot.pool || !standingsByPool.has(orphanSlot.pool)) continue;
      const dbMatch = placementMatches.find((m) => m.draw_match_id === `main-${tm.id}`);
      if (dbMatch) await this.finalizeByeMatch(client, dbMatch);
    }
  },

  /**
   * Calculate standings for a round robin stage using consolidated calculator
   * @param stageId - The stage ID
   * @returns Array of player standings sorted by rank
   */
  async calculateRoundRobinStandings(stageId: string): Promise<
    Array<{
      playerId: string;
      matchesPlayed: number;
      matchesWon: number;
      matchesLost: number;
      gamesWon: number;
      gamesLost: number;
      pointsWon: number;
      pointsLost: number;
    }>
  > {
    // Get stage to access draw_id and stage_id
    const stage = await this.getDrawStageById(stageId);
    if (!stage) {
      throw new Error(`Stage not found: ${stageId}`);
    }

    // Get rounds and matches for this stage
    const rounds = await this.getDrawRoundsByStageId(stageId);

    // Get matches with player data for this stage
    const matches = await this.getDrawMatches(stage.draw_id, stageId);

    // Get draw settings
    const drawSettings = await this.getDrawSettings(stage.draw_id);
    if (!drawSettings) {
      throw new Error('Draw not found');
    }
    const drawOptions = ((drawSettings?.options as DrawOptions | undefined) || {}) as DrawOptions;
    // The same per-stage rule as the display standings, so advancement seeds placement matches
    // under the scoring the standings UI shows: a pool by "Pool scoring", a round-robin main
    // draw by its own block (Pool Play-offs' single block writes the main key).
    const roundRobinSettings = resolveRoundRobinSettings(drawOptions, stage);
    const scoringSystem = roundRobinSettings?.scoringSystem ?? 'standard';
    const tiebreakerOrder = roundRobinSettings?.tiebreakerOrder ?? ['games_ratio', 'points_ratio'];

    // Use consolidated calculator
    const stageStandings = calculateRoundRobinStandings({
      stage: {
        id: stage.id,
        stage_id: stage.stage_id,
        name: stage.name,
        type: stage.type,
      },
      rounds: rounds.map((round) => ({
        id: round.id,
        stage_id: round.stage_id,
        round_id: round.round_id || null,
        name: round.name,
        round_number: round.round_number,
      })),
      matches: (matches || []).map((match) => ({
        id: match.id,
        round_id: match.round_id,
        player_1: match.player_1
          ? {
              id: match.player_1.id,
              first_name: match.player_1.first_name,
              last_name: match.player_1.last_name,
            }
          : null,
        player_2: match.player_2
          ? {
              id: match.player_2.id,
              first_name: match.player_2.first_name,
              last_name: match.player_2.last_name,
            }
          : null,
        winner: match.winner
          ? {
              id: match.winner.id,
              first_name: match.winner.first_name,
              last_name: match.winner.last_name,
            }
          : null,
        status: match.status,
        score: match.score,
        is_bye: match.is_bye,
        event_match_schedule: match.event_match_schedule || null,
      })),
      scoringSystem,
      tiebreakerOrder,
    });

    // Transform to expected format
    return stageStandings.standings.map((standing) => ({
      playerId: standing.playerId,
      matchesPlayed: standing.wins + standing.losses,
      matchesWon: standing.wins,
      matchesLost: standing.losses,
      gamesWon: standing.gamesWon,
      gamesLost: standing.gamesLost,
      pointsWon: standing.gamePointsFor,
      pointsLost: standing.gamePointsAgainst,
    }));
  },
};

export default drawModel;
